import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  PenTool, Image as ImageIcon, Save, Trash2, Grid, PlayCircle, X,
  Eye, Edit2, Plus, Printer, Send, Mail, Phone, Undo, Redo,
  ZoomIn, ZoomOut, Search, Scissors, Wand2, Maximize2, Minimize2,
  Circle, Square, Minus, Eraser, MousePointer, Copy
} from 'lucide-react';
import ShapeBuilder, { SpecDrivenShape } from './ShapeBuilder';

// -------------------------------------------------------------
//  UTILITIES
// -------------------------------------------------------------
const fileToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

// Pixel-level white/near-white background removal
const removeWhiteBackground = (src, threshold = 240) =>
  new Promise((resolve) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width; canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < data.data.length; i += 4) {
        const r = data.data[i], g = data.data[i+1], b = data.data[i+2];
        if (r >= threshold && g >= threshold && b >= threshold) data.data[i+3] = 0;
      }
      ctx.putImageData(data, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(src);
    img.src = src;
  });

// -------------------------------------------------------------
//  SMV ENGINE  (unchanged from previous version)
// -------------------------------------------------------------
const calcBasicSecs = (op, machines, manualCodes) => {
  const freq = parseFloat(op.frequency) || 1;
  if (op.type === 'manual') {
    const ref = (manualCodes || []).find(m => m.id == op.refId);
    return parseFloat(ref?.value || 0) * freq;
  }
  if (op.type === 'machine') {
    const mc = (machines || []).find(m => m.id == op.mcId);
    if (!mc) return 0;
    if (op.directTime) return parseFloat(op.directTime) * freq;
    const RPM = parseFloat(mc.rpm) || 4500;
    const SPC = parseFloat(mc.spc) || 5;
    const McA = parseFloat(mc.allowance) || 10;
    const Len = parseFloat(op.length) || 0;
    const Stop = parseFloat(op.stoppage) || 0;
    const Fab = parseFloat(op.fabric) || 0;
    const sew = ((Len * SPC) / RPM) * 60;
    return sew * (1 + McA / 100) * (1 + Stop / 100) * (1 + Fab / 100) * freq;
  }
  return 0;
};

const calcProcessSMV = (proc, db) => {
  const basicSecs = (proc.ops || []).reduce((s, op) => s + calcBasicSecs(op, db.machines, db.manualCodes), 0);
  return (basicSecs * 1.03 * 1.11 / 60) + 0.09;
};

const totalPartSMV = (processes, db) =>
  (processes || []).reduce((s, p) => s + calcProcessSMV(p, db), 0);

// -------------------------------------------------------------
//  CANVAS SKETCH ENGINE  (completely rewritten)
// -------------------------------------------------------------

/**
 * SketchCanvas - a full-featured vector-style drawing board.
 *
 * Architecture:
 *  * mainCanvas   - the permanent raster layer (background image + committed strokes)
 *  * overlayCanvas - live preview of the current stroke / shape / nodes
 *  * All drawing happens in "canvas-space"; zoom/pan are CSS transforms only,
 *    so the exported image is always at full resolution (800x800).
 *
 * Tools: freehand pen, eraser (destination-out so it's truly transparent),
 *        line, rect, ellipse, bezier (click nodes -> close), node-drag mode.
 *
 * Image tracing: loads a reference image at low opacity so the user can
 *        sketch on top; the reference is NOT included in the final export.
 *
 * AI Sketch: calls the Anthropic API (vision -> "convert to line drawing").
 */

const CANVAS_W = 800;
const CANVAS_H = 800;

/**
 * LAYER ARCHITECTURE (bottom -> top, all absolutely positioned):
 *
 *  [0] bgCanvas   zIndex:1  - permanent white fill, NEVER drawn on after init.
 *                             Acts as the white paper. Eraser CANNOT touch it.
 *  [1] traceImg   zIndex:2  - reference photo at reduced opacity, CSS only, no canvas.
 *                             Sits ABOVE bg but BELOW the stroke canvas so it is
 *                             always visible through the transparent parts of drawCanvas.
 *  [2] drawCanvas zIndex:3  - starts 100% transparent. Strokes are painted here.
 *                             Eraser uses destination-out to remove only stroke pixels,
 *                             revealing the white bgCanvas below - trace stays visible
 *                             only through the draw layer, never "leaks" through erased spots.
 *  [3] overlayCanvas zIndex:4 - live preview / eraser ring, cleared each frame.
 *                             Receives all mouse events.
 *
 * Export: composite bgCanvas + drawCanvas onto a temp canvas -> white background + strokes only.
 */
function SketchCanvas({ onSave, initialImage }) {
  const bgRef      = useRef(null); // white background - never erased
  const drawRef    = useRef(null); // strokes only - transparent bg
  const overlayRef = useRef(null); // live preview
  const containerRef = useRef(null);

  const [tool, setTool]             = useState('pen');
  const [color, setColor]           = useState('#323338');
  const [lineW, setLineW]           = useState(2);
  const [eraserSize, setEraserSize] = useState(20);
  const [zoom, setZoom]             = useState(1);
  const [pan, setPan]               = useState({ x: 0, y: 0 });

  const history   = useRef([]);
  const redoStack = useRef([]);

  const isDown          = useRef(false);
  const startPt         = useRef({ x: 0, y: 0 });
  const bezierPts       = useRef([]);
  const isPanning       = useRef(false);
  const panStart        = useRef({ x: 0, y: 0 });
  const panOrigin       = useRef({ x: 0, y: 0 });
  const smoothBuf       = useRef([]);   // moving-average buffer for freehand smoothing
  const rawPenPts       = useRef([]);   // raw points for post-stroke Catmull-Rom fit

  const [traceImg, setTraceImg]           = useState(null);
  const [traceOpacity, setTraceOpacity]   = useState(0.3);
  const [traceInExport, setTraceInExport] = useState(true); // bake reference into saved image
  const traceImgElRef                     = useRef(null);   // ref to the DOM <img> for export

  // -- Trace image transform (drag, resize, rotate with anchor handles) ------
  const [traceTransform, setTraceTransform] = useState({ x: 0, y: 0, w: 0, h: 0, rot: 0 });
  const [traceNatW, setTraceNatW] = useState(0); // natural width for aspect ratio
  const [traceNatH, setTraceNatH] = useState(0);
  const [lockAspect, setLockAspect] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [gridSize, setGridSize] = useState(40); // pixels per grid cell
  const [snapToGrid, setSnapToGrid] = useState(false);
  const traceDragRef = useRef(null); // { type: 'move'|'rotate'|'resize-br'|'resize-bl', startX, startY, origTransform }

  // Reset trace transform when new image loaded
  const loadTraceImage = (src) => {
    const img = new Image();
    img.onload = () => {
      const aspect = img.naturalWidth / img.naturalHeight;
      const h = Math.min(CANVAS_H * 0.8, img.naturalHeight);
      const w = h * aspect;
      setTraceNatW(img.naturalWidth);
      setTraceNatH(img.naturalHeight);
      setTraceTransform({ x: (CANVAS_W - w) / 2, y: (CANVAS_H - h) / 2, w, h, rot: 0 });
      setTraceImg(src);
    };
    img.src = src;
  };

  // Handle mouse events on the trace image overlay
  const onTraceMouseDown = (e, type) => {
    e.stopPropagation();
    const rect = e.target.closest('[data-trace-container]')?.getBoundingClientRect();
    traceDragRef.current = { type, startX: e.clientX, startY: e.clientY, origTransform: { ...traceTransform }, rect };
    const onMove = (me) => {
      const dx = (me.clientX - traceDragRef.current.startX) / zoom;
      const dy = (me.clientY - traceDragRef.current.startY) / zoom;
      const orig = traceDragRef.current.origTransform;
      setTraceTransform(prev => {
        let t = { ...prev };
        if (traceDragRef.current.type === 'move') {
          t.x = orig.x + dx; t.y = orig.y + dy;
          if (snapToGrid) { t.x = Math.round(t.x / gridSize) * gridSize; t.y = Math.round(t.y / gridSize) * gridSize; }
        } else if (traceDragRef.current.type === 'rotate') {
          // Angle from centre of image to mouse
          const cx = orig.x + orig.w / 2;
          const cy = orig.y + orig.h / 2;
          const contRect = traceDragRef.current.rect;
          // mouse relative to canvas (account for pan/zoom)
          const mouseCanvasX = (me.clientX - (contRect?.left || 0)) / zoom - pan.x / zoom;
          const mouseCanvasY = (me.clientY - (contRect?.top  || 0)) / zoom - pan.y / zoom;
          const angle = Math.atan2(mouseCanvasY - cy, mouseCanvasX - cx);
          t.rot = angle; // radians
        } else if (traceDragRef.current.type === 'resize-br') {
          const newW = Math.max(30, orig.w + dx);
          t.w = newW;
          t.h = lockAspect && traceNatW > 0 ? newW * (traceNatH / traceNatW) : Math.max(30, orig.h + dy);
        } else if (traceDragRef.current.type === 'resize-bl') {
          const newW = Math.max(30, orig.w - dx);
          const diffW = newW - orig.w;
          t.w = newW; t.x = orig.x - diffW;
          t.h = lockAspect && traceNatW > 0 ? newW * (traceNatH / traceNatW) : Math.max(30, orig.h + dy);
        }
        return t;
      });
    };
    const onUp = () => { traceDragRef.current = null; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const [aiLoading, setAiLoading]         = useState(false);
  const [aiError, setAiError]             = useState('');

  // -- Fullscreen & Texture --
  const [isFullscreen, setIsFullscreen]   = useState(false);
  const [fillTexture, setFillTexture]     = useState(null);   // base64 texture image
  const [fillEnabled, setFillEnabled]     = useState(false);  // fill shapes with texture
  const texturePatternRef                 = useRef(null);     // CanvasPattern object

  // Build / rebuild CanvasPattern whenever texture changes
  useEffect(() => {
    if (!fillTexture) { texturePatternRef.current = null; return; }
    const img = new window.Image();
    img.onload = () => {
      const tmp = document.createElement('canvas');
      tmp.width = img.width; tmp.height = img.height;
      tmp.getContext('2d').drawImage(img, 0, 0);
      // pattern is built against drawRef context when first needed
      texturePatternRef.current = { img, tmpCanvas: tmp };
    };
    img.src = fillTexture;
  }, [fillTexture]);

  // -- Init --------------------------------------------------
  useEffect(() => {
    // Paint bgCanvas white once - this is the permanent "paper"
    const bgCtx = bgRef.current.getContext('2d');
    bgCtx.fillStyle = '#ffffff';
    bgCtx.fillRect(0, 0, CANVAS_W, CANVAS_H);

    // drawCanvas stays fully transparent by default.
    // If editing an existing part, render its image onto drawCanvas.
    if (initialImage) {
      const img = new window.Image();
      img.onload = () => {
        drawRef.current.getContext('2d').drawImage(img, 0, 0, CANVAS_W, CANVAS_H);
        pushHistory();
      };
      img.src = initialImage;
    } else {
      pushHistory();
    }
  }, []); // eslint-disable-line

  // -- History - snapshots of drawCanvas only -----------------
  const pushHistory = useCallback(() => {
    const ctx = drawRef.current?.getContext('2d');
    if (!ctx) return;
    const snap = ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
    history.current = [...history.current.slice(-29), snap];
    redoStack.current = [];
  }, []);

  const undo = useCallback(() => {
    if (history.current.length < 2) return;
    redoStack.current.push(history.current.pop());
    drawRef.current.getContext('2d').putImageData(history.current[history.current.length - 1], 0, 0);
  }, []);

  const redo = useCallback(() => {
    if (!redoStack.current.length) return;
    const snap = redoStack.current.pop();
    drawRef.current.getContext('2d').putImageData(snap, 0, 0);
    history.current.push(snap);
  }, []);

  // -- Coordinate mapping -------------------------------------
  const toCanvas = useCallback((e) => {
    const rect = overlayRef.current.getBoundingClientRect();
    return { x: (e.clientX - rect.left) / zoom, y: (e.clientY - rect.top) / zoom };
  }, [zoom]);

  // -- Overlay helpers ----------------------------------------
  const clearOverlay = useCallback(() => {
    overlayRef.current?.getContext('2d').clearRect(0, 0, CANVAS_W, CANVAS_H);
  }, []);

  // -- Texture pattern builder --------------------------------
  const getTexturePattern = useCallback((ctx) => {
    if (!texturePatternRef.current) return null;
    const { tmpCanvas } = texturePatternRef.current;
    return ctx.createPattern(tmpCanvas, 'repeat');
  }, []);

  // -- Flood fill (paint bucket) ------------------------------
  const floodFill = useCallback((ctx, startX, startY, fillStyle) => {
    startX = Math.round(startX); startY = Math.round(startY);
    const imageData = ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
    const data = imageData.data;
    const idx = (x, y) => (y * CANVAS_W + x) * 4;
    const si = idx(startX, startY);
    const targetR = data[si], targetG = data[si+1], targetB = data[si+2], targetA = data[si+3];

    // Draw fill onto a temp canvas to get pixel data
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = CANVAS_W; tempCanvas.height = CANVAS_H;
    const tempCtx = tempCanvas.getContext('2d');
    if (fillStyle instanceof CanvasPattern) {
      tempCtx.fillStyle = fillStyle;
    } else {
      tempCtx.fillStyle = fillStyle;
    }
    tempCtx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    const fillData = tempCtx.getImageData(0, 0, CANVAS_W, CANVAS_H).data;

    const matchTarget = (i) =>
      Math.abs(data[i]   - targetR) < 32 &&
      Math.abs(data[i+1] - targetG) < 32 &&
      Math.abs(data[i+2] - targetB) < 32 &&
      Math.abs(data[i+3] - targetA) < 32;

    const visited = new Uint8Array(CANVAS_W * CANVAS_H);
    const queue = [startX + startY * CANVAS_W];
    visited[startX + startY * CANVAS_W] = 1;

    while (queue.length) {
      const pos = queue.pop();
      const x = pos % CANVAS_W, y = (pos / CANVAS_W) | 0;
      const i = idx(x, y);
      // Write fill pixel
      data[i]   = fillData[i];
      data[i+1] = fillData[i+1];
      data[i+2] = fillData[i+2];
      data[i+3] = 255;

      const neighbors = [
        x > 0              ? pos - 1          : -1,
        x < CANVAS_W - 1   ? pos + 1          : -1,
        y > 0              ? pos - CANVAS_W   : -1,
        y < CANVAS_H - 1   ? pos + CANVAS_W   : -1,
      ];
      for (const n of neighbors) {
        if (n >= 0 && !visited[n] && matchTarget(idx(n % CANVAS_W, (n / CANVAS_W) | 0))) {
          visited[n] = 1;
          queue.push(n);
        }
      }
    }
    ctx.putImageData(imageData, 0, 0);
  }, []);

  const drawShapePreview = useCallback((x, y) => {
    const oc = overlayRef.current?.getContext('2d');
    if (!oc) return;
    oc.clearRect(0, 0, CANVAS_W, CANVAS_H);
    oc.strokeStyle = color; oc.lineWidth = lineW;
    oc.setLineDash([4, 3]);
    const sx = startPt.current.x, sy = startPt.current.y;
    const dx = x - sx, dy = y - sy;
    oc.beginPath();
    if (tool === 'line')    { oc.moveTo(sx, sy); oc.lineTo(x, y); }
    else if (tool === 'rect')    { oc.rect(sx, sy, dx, dy); }
    else if (tool === 'ellipse') { oc.ellipse(sx + dx / 2, sy + dy / 2, Math.abs(dx / 2), Math.abs(dy / 2), 0, 0, Math.PI * 2); }
    oc.stroke(); oc.setLineDash([]);
  }, [tool, color, lineW]);

  // -- Mouse down ---------------------------------------------
  const onMouseDown = useCallback((e) => {
    if (e.button === 1 || e.altKey) {
      isPanning.current = true;
      panStart.current  = { x: e.clientX, y: e.clientY };
      panOrigin.current = { ...pan };
      return;
    }

    const pt  = toCanvas(e);
    // All drawing targets drawCanvas (transparent layer)
    const ctx = drawRef.current.getContext('2d');

    // -- Bucket / flood fill tool --
    if (tool === 'bucket') {
      pushHistory();
      const ctx2 = drawRef.current.getContext('2d');
      // -- KEY FIX ----------------------------------------------------------
      // Sample ONLY drawCanvas (NOT bgCanvas). The white bgCanvas looks
      // identical inside AND outside every shape, so compositing it makes
      // flood fill unable to distinguish inside from outside -> floods
      // the entire canvas including white background.
      //
      // Instead we use ALPHA as the boundary signal on drawCanvas only:
      //   alpha < 30  -> empty / transparent -> fillable region
      //   alpha >= 30  -> stroke pixel        -> boundary (stop here)
      //
      // We dilate stroke pixels by 1 px to bridge hairline gaps in
      // freehand shapes before filling (prevents leaking outside).
      // ---------------------------------------------------------------------
      const drawData = ctx2.getImageData(0, 0, CANVAS_W, CANVAS_H);
      const dd = drawData.data;

      const startX = Math.round(pt.x), startY = Math.round(pt.y);
      const si = (startY * CANVAS_W + startX) * 4;

      // Clicked directly on a stroke pixel -- nothing to fill
      if (dd[si + 3] >= 30) return;

      // Build 1-px dilated stroke mask to close hairline gaps
      const strokeMask = new Uint8Array(CANVAS_W * CANVAS_H);
      for (let y = 0; y < CANVAS_H; y++) {
        for (let x = 0; x < CANVAS_W; x++) {
          if (dd[(y * CANVAS_W + x) * 4 + 3] >= 30) {
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                const nx = x + dx, ny = y + dy;
                if (nx >= 0 && nx < CANVAS_W && ny >= 0 && ny < CANVAS_H)
                  strokeMask[ny * CANVAS_W + nx] = 1;
              }
            }
          }
        }
      }

      // Build fill pixel data from texture or solid colour
      const texPat = fillTexture ? getTexturePattern(ctx2) : null;
      const tempC = document.createElement('canvas');
      tempC.width = CANVAS_W; tempC.height = CANVAS_H;
      const tc = tempC.getContext('2d');
      if (texPat) { tc.fillStyle = texPat; } else { tc.fillStyle = color; }
      tc.fillRect(0, 0, CANVAS_W, CANVAS_H);
      const fillPx = tc.getImageData(0, 0, CANVAS_W, CANVAS_H).data;

      // Flood fill -- enter only transparent, non-stroke pixels
      const visited = new Uint8Array(CANVAS_W * CANVAS_H);
      const queue = [startX + startY * CANVAS_W];
      visited[startX + startY * CANVAS_W] = 1;
      while (queue.length) {
        const pos = queue.pop();
        const fx = pos % CANVAS_W, fy = (pos / CANVAS_W) | 0;
        const i = (fy * CANVAS_W + fx) * 4;
        dd[i] = fillPx[i]; dd[i+1] = fillPx[i+1]; dd[i+2] = fillPx[i+2]; dd[i+3] = 255;
        const nb = [
          fx > 0            ? pos - 1        : -1,
          fx < CANVAS_W - 1 ? pos + 1        : -1,
          fy > 0            ? pos - CANVAS_W : -1,
          fy < CANVAS_H - 1 ? pos + CANVAS_W : -1,
        ];
        for (const n of nb) {
          if (n >= 0 && !visited[n] && !strokeMask[n]) {
            visited[n] = 1;
            queue.push(n);
          }
        }
      }
      ctx2.putImageData(drawData, 0, 0);
      return;
    }

    if (tool === 'bezier') {
      const oc = overlayRef.current.getContext('2d');
      if (e.detail === 2 && bezierPts.current.length >= 2) {
        // Commit bezier to drawCanvas
        pushHistory();
        const ctx2 = drawRef.current.getContext('2d');
        ctx2.strokeStyle = color; ctx2.lineWidth = lineW;
        ctx2.lineCap = 'round'; ctx2.lineJoin = 'round';
        ctx2.beginPath();
        const pts = bezierPts.current;
        ctx2.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length - 1; i++) {
          const mx = (pts[i].x + pts[i+1].x) / 2;
          const my = (pts[i].y + pts[i+1].y) / 2;
          ctx2.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
        }
        ctx2.lineTo(pts[pts.length - 1].x, pts[pts.length - 1].y);
        ctx2.closePath();
        if (fillEnabled && fillTexture) {
          const pat = getTexturePattern(ctx2);
          if (pat) { ctx2.fillStyle = pat; ctx2.fill(); }
        } else if (fillEnabled) {
          ctx2.fillStyle = color; ctx2.fill();
        }
        ctx2.stroke();
        bezierPts.current = []; oc.clearRect(0, 0, CANVAS_W, CANVAS_H);
      } else {
        bezierPts.current.push(pt);
        oc.clearRect(0, 0, CANVAS_W, CANVAS_H);
        oc.strokeStyle = color; oc.lineWidth = lineW; oc.setLineDash([3, 3]);
        const pts = bezierPts.current;
        oc.beginPath(); oc.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) oc.lineTo(pts[i].x, pts[i].y);
        oc.stroke(); oc.setLineDash([]);
        pts.forEach(p => { oc.beginPath(); oc.arc(p.x, p.y, 4, 0, Math.PI * 2); oc.fillStyle = '#0073ea'; oc.fill(); });
      }
      return;
    }

    isDown.current  = true;
    startPt.current = pt;
    pushHistory();

    if (tool === 'pen') {
      ctx.strokeStyle = color; ctx.lineWidth = lineW;
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      ctx.globalCompositeOperation = 'source-over';
      ctx.beginPath(); ctx.moveTo(pt.x, pt.y);
      smoothBuf.current = [pt];
      rawPenPts.current = [pt];
    }

    if (tool === 'eraser') {
      // destination-out on drawCanvas removes stroke pixels only.
      // bgCanvas is a separate element - it is physically impossible for
      // destination-out on drawCanvas to affect it.
      ctx.globalCompositeOperation = 'destination-out';
      ctx.lineWidth = eraserSize; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(pt.x, pt.y);
    }
  }, [tool, color, lineW, eraserSize, pan, toCanvas, pushHistory, fillEnabled, fillTexture, getTexturePattern]);

  // -- Mouse move ---------------------------------------------
  const onMouseMove = useCallback((e) => {
    if (isPanning.current) {
      setPan({ x: panOrigin.current.x + e.clientX - panStart.current.x, y: panOrigin.current.y + e.clientY - panStart.current.y });
      return;
    }

    const pt = toCanvas(e);
    const oc = overlayRef.current?.getContext('2d');

    // Always show eraser ring cursor
    if (tool === 'eraser') {
      if (oc) {
        oc.clearRect(0, 0, CANVAS_W, CANVAS_H);
        oc.strokeStyle = '#e2445c'; oc.lineWidth = 1.5;
        oc.beginPath(); oc.arc(pt.x, pt.y, eraserSize / 2, 0, Math.PI * 2); oc.stroke();
      }
    }

    if (!isDown.current) return;

    const ctx = drawRef.current.getContext('2d');

    if (tool === 'pen') {
      // Moving-average smoothing: keeps last 6 points, draws to their centroid.
      // This eliminates the jagged look of raw freehand strokes while preserving
      // fast direction changes.
      smoothBuf.current.push(pt);
      rawPenPts.current.push(pt);
      if (smoothBuf.current.length > 6) smoothBuf.current.shift();
      const avgX = smoothBuf.current.reduce((s, p) => s + p.x, 0) / smoothBuf.current.length;
      const avgY = smoothBuf.current.reduce((s, p) => s + p.y, 0) / smoothBuf.current.length;
      ctx.lineTo(avgX, avgY); ctx.stroke();
    } else if (tool === 'eraser') {
      // Erase on drawCanvas only - bg and trace are untouched
      ctx.lineTo(pt.x, pt.y); ctx.stroke();
    } else if (['line', 'rect', 'ellipse'].includes(tool)) {
      // Shift-key: snap to nearest 45deg angle for lines
      if (tool === 'line' && e.shiftKey) {
        const dx = pt.x - startPt.current.x;
        const dy = pt.y - startPt.current.y;
        const angle = Math.atan2(dy, dx);
        const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.hypot(dx, dy);
        drawShapePreview(
          startPt.current.x + Math.cos(snapped) * dist,
          startPt.current.y + Math.sin(snapped) * dist
        );
      } else {
        drawShapePreview(pt.x, pt.y);
      }
    }
  }, [tool, eraserSize, toCanvas, drawShapePreview]);

  // -- Mouse up -----------------------------------------------
  const onMouseUp = useCallback((e) => {
    if (isPanning.current) { isPanning.current = false; return; }
    if (!isDown.current) return;
    isDown.current = false;

    const pt  = toCanvas(e);
    const ctx = drawRef.current.getContext('2d');

    if (tool === 'eraser') {
      ctx.globalCompositeOperation = 'source-over'; // always reset
      ctx.closePath(); clearOverlay(); return;
    }

    if (tool === 'pen') { ctx.closePath(); return; }

    // Commit shape to drawCanvas
    const sx = startPt.current.x, sy = startPt.current.y;
    const dx = pt.x - sx, dy = pt.y - sy;
    ctx.strokeStyle = color; ctx.lineWidth = lineW;
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.globalCompositeOperation = 'source-over';
    ctx.beginPath();
    if (tool === 'line') {
      let lx = pt.x, ly = pt.y;
      if (e.shiftKey) {
        const dx = pt.x - sx, dy = pt.y - sy;
        const angle = Math.atan2(dy, dx);
        const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        const dist = Math.hypot(dx, dy);
        lx = sx + Math.cos(snapped) * dist;
        ly = sy + Math.sin(snapped) * dist;
      }
      ctx.moveTo(sx, sy); ctx.lineTo(lx, ly);
    }
    else if (tool === 'rect')    { ctx.rect(sx, sy, dx, dy); }
    else if (tool === 'ellipse') { ctx.ellipse(sx + dx/2, sy + dy/2, Math.abs(dx/2), Math.abs(dy/2), 0, 0, Math.PI*2); }
    // Apply texture/color fill for closed shapes
    if (tool !== 'line' && fillEnabled) {
      if (fillTexture) {
        const pat = getTexturePattern(ctx);
        if (pat) { ctx.fillStyle = pat; ctx.fill(); }
      } else {
        ctx.fillStyle = color; ctx.fill();
      }
    }
    ctx.stroke(); clearOverlay();
  }, [tool, color, lineW, toCanvas, clearOverlay, fillEnabled, fillTexture, getTexturePattern]);

  const onMouseLeave = useCallback(() => {
    if (isDown.current) {
      isDown.current = false;
      const ctx = drawRef.current.getContext('2d');
      if (tool === 'eraser') ctx.globalCompositeOperation = 'source-over';
      if (tool === 'pen' || tool === 'eraser') {/* already pushed on down */}
    }
    isPanning.current = false;
    clearOverlay();
  }, [tool, clearOverlay]);

  // -- Zoom / pan ---------------------------------------------
  const onWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => Math.min(4, Math.max(0.25, +(z + (e.deltaY > 0 ? -0.1 : 0.1)).toFixed(2))));
  }, []);

  // -- Keyboard shortcuts -------------------------------------
  useEffect(() => {
    const h = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') { e.preventDefault(); undo(); }
      if ((e.ctrlKey || e.metaKey) && e.key === 'y') { e.preventDefault(); redo(); }
      if (e.key === 'Escape') { bezierPts.current = []; clearOverlay(); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [undo, redo, clearOverlay]);

  // -- Load trace image from file input -----------------------
  const handleTraceFileInput = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    loadTraceImage(await fileToBase64(file));
  };

  // -- AI Auto-Sketch -----------------------------------------
  const runAISketch = async () => {
    if (!traceImg) return alert('Load a reference image first.');
    setAiLoading(true); setAiError('');
    try {
      const base64Data = traceImg.split(',')[1];
      const mediaType  = traceImg.split(';')[0].split(':')[1];

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-access': 'true',
        },
        body: JSON.stringify({
          model: 'claude-opus-4-5',
          max_tokens: 4000,
          messages: [{
            role: 'user',
            content: [
              {
                type: 'image',
                source: { type: 'base64', media_type: mediaType, data: base64Data }
              },
              {
                type: 'text',
                text: `You are a professional garment technical flat sketch artist. Study this image carefully and produce a clean SVG technical line drawing of the garment part.

STRICT REQUIREMENTS:
- Output a single SVG with viewBox="0 0 800 800" xmlns="http://www.w3.org/2000/svg"
- Draw ONLY the outline silhouette and major internal seam lines / construction details
- Use ONLY <path> elements. No <rect>, no <circle>, no <text>, no fills
- Every path must have: stroke="#323338" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
- Scale the drawing to fill roughly 80% of the 800x800 space, centred
- Trace the exact shape including any curves, notches, asymmetry - do NOT simplify to a rectangle
- For curved edges use cubic bezier curves (C command) in the path data for accuracy
- Include stitch lines, fold lines, seam allowance lines as separate lighter paths (stroke-width="1" stroke-dasharray="4,3")

Return ONLY the raw SVG XML. Start with <svg and end with </svg>. Zero explanation, zero markdown, zero code fences.`
              }
            ]
          }]
        })
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `API error ${response.status}`);
      }

      const data    = await response.json();
      const svgText = data.content?.find(b => b.type === 'text')?.text?.trim() || '';

      if (!svgText.startsWith('<svg')) {
        // Try to extract SVG if wrapped in extra text
        const match = svgText.match(/<svg[\s\S]*<\/svg>/i);
        if (!match) throw new Error('AI did not return valid SVG. Try again or adjust the reference image.');
      }

      const finalSvg = svgText.startsWith('<svg')
        ? svgText
        : svgText.match(/<svg[\s\S]*<\/svg>/i)[0];

      // Render SVG onto drawCanvas
      const blob = new Blob([finalSvg], { type: 'image/svg+xml;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const img  = new window.Image();
      img.onload = () => {
        pushHistory();
        const ctx = drawRef.current.getContext('2d');
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H);
        URL.revokeObjectURL(url);
        setAiLoading(false);
      };
      img.onerror = () => {
        // SVG may have CORS or security issues rendering via Image - fallback: inline render
        const svgBlob = new Blob([finalSvg], { type: 'image/svg+xml' });
        const reader  = new FileReader();
        reader.onload = (ev) => {
          const img2 = new window.Image();
          img2.onload = () => {
            pushHistory();
            const ctx = drawRef.current.getContext('2d');
            ctx.globalCompositeOperation = 'source-over';
            ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
            ctx.drawImage(img2, 0, 0, CANVAS_W, CANVAS_H);
            setAiLoading(false);
          };
          img2.onerror = () => { setAiError('SVG could not be rendered to canvas.'); setAiLoading(false); };
          img2.src = ev.target.result;
        };
        reader.readAsDataURL(svgBlob);
      };
      img.src = url;

    } catch (err) {
      setAiError(err.message || 'AI sketch failed - check console');
      console.error('AI Sketch error:', err);
      setAiLoading(false);
    }
  };

  // -- Clear strokes ------------------------------------------
  const clearAll = () => {
    if (!window.confirm('Clear all strokes?')) return;
    pushHistory();
    const ctx = drawRef.current.getContext('2d');
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  };

  // -- Export - white bg + optional trace + strokes ----------
  const exportImage = () => {
    const exp = document.createElement('canvas');
    exp.width = CANVAS_W; exp.height = CANVAS_H;
    const ec = exp.getContext('2d');

    // 1. White background
    ec.drawImage(bgRef.current, 0, 0);

    // 2. Bake trace image at its display opacity (only if checkbox is ticked)
    if (traceImg && traceInExport && traceImgElRef.current) {
      ec.globalAlpha = traceOpacity;
      const { x, y, w, h, rot } = traceTransform;
      const cx = x + w / 2, cy = y + h / 2;
      ec.save();
      ec.translate(cx, cy);
      ec.rotate(rot);
      ec.drawImage(traceImgElRef.current, -w/2, -h/2, w, h);
      ec.restore();
      ec.globalAlpha = 1;
    }

    // 3. Strokes on top
    ec.drawImage(drawRef.current, 0, 0);

    onSave(exp.toDataURL('image/png'));
  };

  const cursorMap = { pen: 'crosshair', eraser: 'none', line: 'crosshair', rect: 'crosshair', ellipse: 'crosshair', bezier: 'cell', bucket: 'cell' };

  const TOOLS = [
    { id: 'pen',     label: 'Pen',         icon: <PenTool size={14} /> },
    { id: 'eraser',  label: 'Eraser',      icon: <Eraser size={14} /> },
    { id: 'line',    label: 'Line',        icon: <Minus size={14} /> },
    { id: 'rect',    label: 'Rect',        icon: <Square size={14} /> },
    { id: 'ellipse', label: 'Ellipse',     icon: <Circle size={14} /> },
    { id: 'bezier',  label: 'Free Shape',  icon: <MousePointer size={14} /> },
    { id: 'bucket',  label: 'Fill',        icon: <span style={{ fontSize: '13px' }}>Fill</span> },
  ];

  // -- Canvas panel (shared between normal and fullscreen) ----
  const canvasPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#323338', borderRadius: isFullscreen ? '0' : '8px', overflow: 'hidden' }}>
      {/* -- Toolbar -- */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', background: '#1f1f1f', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '3px', background: '#323338', borderRadius: '6px', padding: '3px' }}>
          {TOOLS.map(t => (
            <button key={t.id} title={t.label} onClick={() => { setTool(t.id); bezierPts.current = []; clearOverlay(); }}
              style={{ padding: '5px 8px', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.72rem',
                background: tool === t.id ? '#0073ea' : 'transparent', color: tool === t.id ? 'white' : '#9699a6' }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9699a6', fontSize: '0.72rem' }}>
          Color <input type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: '26px', height: '26px', border: '2px solid #676879', padding: '1px', borderRadius: '6px', cursor: 'pointer' }} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9699a6', fontSize: '0.72rem' }}>
          W: <input type="range" min="1" max="20" value={lineW} onChange={e => setLineW(Number(e.target.value))} style={{ width: '60px' }} />
          <span style={{ color: 'white', minWidth: '16px' }}>{lineW}</span>
        </label>
        {tool === 'eraser' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#9699a6', fontSize: '0.72rem' }}>
            Size: <input type="range" min="5" max="80" value={eraserSize} onChange={e => setEraserSize(Number(e.target.value))} style={{ width: '60px' }} />
            <span style={{ color: 'white', minWidth: '20px' }}>{eraserSize}</span>
          </label>
        )}
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        <button onClick={undo} title="Undo (Ctrl+Z)" style={tbtn()}><Undo size={13} /></button>
        <button onClick={redo} title="Redo (Ctrl+Y)" style={tbtn()}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13"/></svg>
        </button>
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        <button onClick={() => setZoom(z => Math.min(4, +(z + 0.25).toFixed(2)))} style={tbtn()} title="Zoom In"><ZoomIn size={13} /></button>
        <span style={{ color: '#9699a6', fontSize: '0.72rem', minWidth: '36px', textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
        <button onClick={() => setZoom(z => Math.max(0.25, +(z - 0.25).toFixed(2)))} style={tbtn()} title="Zoom Out"><ZoomOut size={13} /></button>
        <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} style={tbtn()} title="Reset View"><Maximize2 size={13} /></button>
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        {/* Grid + Snap controls */}
        <button onClick={() => setShowGrid(g => !g)} title="Toggle grid overlay"
          style={{ ...tbtn(), color: showGrid ? '#4ade80' : '#9699a6', background: showGrid ? '#162616' : 'transparent', fontSize:'0.7rem', gap:3 }}>
          <Grid size={12} /> Grid
        </button>
        {showGrid && (<>
          <select value={gridSize} onChange={e => setGridSize(+e.target.value)}
            style={{ padding:'2px 5px', background:'#1a1f2e', border:'1px solid #323338', color:'#9699a6', borderRadius:4, fontSize:'0.7rem' }}>
            {[10,20,30,40,50,80,100].map(s => <option key={s} value={s}>{s}px</option>)}
          </select>
          <label style={{ display:'flex', gap:3, alignItems:'center', color: snapToGrid?'#4ade80':'#9699a6', fontSize:'0.7rem', cursor:'pointer' }}>
            <input type="checkbox" checked={snapToGrid} onChange={e => setSnapToGrid(e.target.checked)}/>Snap
          </label>
        </>)}
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        <button onClick={() => setIsFullscreen(f => !f)} title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen Canvas'}
          style={{ ...tbtn(), color: isFullscreen ? '#fdab3d' : '#9699a6', border: isFullscreen ? '1px solid #fdab3d' : '1px solid #323338' }}>
          {isFullscreen
            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/></svg>
            : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7V3h4"/><path d="M17 3h4v4"/><path d="M21 17v4h-4"/><path d="M7 21H3v-4"/></svg>
          }
          <span style={{ fontSize: '0.72rem', marginLeft: '3px' }}>{isFullscreen ? 'Exit' : 'Fullscreen'}</span>
        </button>
        <div style={{ width: '1px', height: '24px', background: '#323338' }} />
        <button onClick={clearAll} style={{ ...tbtn(), color: '#fca5a5' }} title="Clear Strokes"><Trash2 size={13} /></button>
        <button onClick={exportImage} style={{ padding: '6px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem', fontWeight: 'bold', marginLeft: 'auto' }}>
          <Save size={13} /> Save to Part
        </button>
      </div>

      {/* -- Texture / Fill row -- */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '5px 10px', background: '#1f1f1f', borderTop: '1px solid #323338', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', color: '#676879', fontWeight: 'bold' }}>FILL:</span>
        {/* Toggle fill for shapes */}
        <button onClick={() => setFillEnabled(f => !f)}
          style={{ padding: '3px 10px', background: fillEnabled ? '#0ea5e9' : '#323338', color: fillEnabled ? 'white' : '#9699a6',
            border: `1px solid ${fillEnabled ? '#0ea5e9' : '#323338'}`, borderRadius: '5px', cursor: 'pointer', fontSize: '0.72rem' }}>
          {fillEnabled ? '(/) Fill On' : 'Fill Off'}
        </button>
        {/* Texture upload */}
        <label style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer', padding: '3px 10px', background: '#323338', borderRadius: '5px', color: '#c5c7d4', fontSize: '0.72rem', border: '1px solid #323338' }}>
          [img] {fillTexture ? 'Change Texture' : 'Load Texture'}
          <input type="file" accept="image/*" hidden onChange={async e => {
            const file = e.target.files[0]; if (!file) return;
            setFillTexture(await fileToBase64(file));
            setFillEnabled(true);
          }} />
        </label>
        {fillTexture && (
          <>
            <img src={fillTexture} alt="texture preview"
              style={{ width: '28px', height: '28px', objectFit: 'cover', borderRadius: '6px', border: '2px solid #0ea5e9' }} />
            <button onClick={() => { setFillTexture(null); }} style={{ ...tbtn(), color: '#fca5a5', padding: '3px 7px', fontSize: '0.7rem' }}>(x) Remove</button>
          </>
        )}
        {!fillTexture && fillEnabled && (
          <span style={{ fontSize: '0.7rem', color: '#9699a6' }}>-&gt; Using solid color fill</span>
        )}
        {(tool === 'bucket') && (
          <span style={{ fontSize: '0.7rem', color: '#fdab3d' }}>
            Fill Click inside any closed shape to flood-fill with {fillTexture ? 'texture' : 'color'}
          </span>
        )}
        {(tool === 'bezier') && (
          <span style={{ color: '#fdab3d', fontSize: '0.7rem' }}>[dot] Click nodes \u00B7 Double-click to close \u00B7 Esc cancel</span>
        )}
      </div>

      {/* -- Trace / AI row -- */}
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '6px 10px', background: '#323338', borderTop: '1px solid #323338', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.72rem', color: '#676879', fontWeight: 'bold' }}>TRACE / AI:</span>
        <label style={{ display: 'flex', gap: '5px', alignItems: 'center', cursor: 'pointer', padding: '4px 10px', background: '#323338', borderRadius: '5px', color: '#c5c7d4', fontSize: '0.72rem' }}>
          <ImageIcon size={12} /> Load Reference Image
          <input type="file" accept="image/*" hidden onChange={handleTraceFileInput} />
        </label>
        {traceImg && (
          <>
            <label style={{ display: 'flex', gap: '4px', alignItems: 'center', color: '#9699a6', fontSize: '0.72rem' }}>
              Ref Opacity: <input type="range" min="5" max="80" value={Math.round(traceOpacity * 100)} onChange={e => setTraceOpacity(e.target.value / 100)} style={{ width: '60px' }} />
              <span style={{ color: 'white', minWidth: '28px' }}>{Math.round(traceOpacity * 100)}%</span>
            </label>
            {/* Aspect ratio lock */}
            <label title="Lock aspect ratio when resizing" style={{ display:'flex', gap:'4px', alignItems:'center', color: lockAspect?'#4ade80':'#9699a6', fontSize:'0.72rem', cursor:'pointer' }}>
              <input type="checkbox" checked={lockAspect} onChange={e => setLockAspect(e.target.checked)} />
              [L] Lock ratio
            </label>
            {/* Reset transform */}
            <button onClick={() => {
              if (traceNatW > 0) {
                const aspect = traceNatW / traceNatH;
                const h = Math.min(CANVAS_H * 0.8, traceNatH);
                const w = h * aspect;
                setTraceTransform({ x:(CANVAS_W-w)/2, y:(CANVAS_H-h)/2, w, h, rot:0 });
              }
            }} style={{ ...tbtn(), fontSize:'0.68rem', color:'#fdab3d' }} title="Reset position, size and rotation">\u27F3 Reset</button>
            <label style={{ display: 'flex', gap: '4px', alignItems: 'center', color: '#9699a6', fontSize: '0.72rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={traceInExport} onChange={e => setTraceInExport(e.target.checked)} />
              <span style={{ color: traceInExport ? '#4ade80' : '#676879' }}>Bake into save</span>
            </label>
            <button onClick={runAISketch} disabled={aiLoading}
              style={{ padding: '4px 12px', background: aiLoading ? '#676879' : '#a25ddc', color: 'white', border: 'none', borderRadius: '5px', cursor: aiLoading ? 'wait' : 'pointer', fontSize: '0.72rem', display: 'flex', gap: '5px', alignItems: 'center' }}>
              <Wand2 size={12} /> {aiLoading ? 'AI Sketching...' : 'AI Auto-Sketch'}
            </button>
            <button onClick={() => setTraceImg(null)} style={{ ...tbtn(), color: '#fca5a5' }} title="Remove reference"><X size={12} /></button>
          </>
        )}
        {aiError && <span style={{ color: '#eb6175', fontSize: '0.7rem' }}>\u26A0 {aiError}</span>}
      </div>

      {/* -- Canvas viewport -- */}
      <div ref={containerRef} style={{ flex: 1, overflow: 'hidden', position: 'relative', background: '#323338', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onWheel={onWheel}>
        <div style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`, transformOrigin: 'center', position: 'relative', width: CANVAS_W, height: CANVAS_H, boxShadow: '0 0 40px rgba(0,0,0,0.6)' }}>

          {/* Layer 0: permanent white background - eraser CANNOT reach this */}
          <canvas ref={bgRef} width={CANVAS_W} height={CANVAS_H}
            style={{ position: 'absolute', inset: 0, zIndex: 1, display: 'block' }} />

          {/* Layer 1: grid overlay */}
          {showGrid && (
            <svg style={{ position:'absolute', inset:0, zIndex:2, pointerEvents:'none', width:CANVAS_W, height:CANVAS_H }} width={CANVAS_W} height={CANVAS_H}>
              {/* Vertical lines */}
              {Array.from({ length: Math.ceil(CANVAS_W / gridSize) + 1 }, (_, i) => i * gridSize).map(x => (
                <line key={'v'+x} x1={x} y1={0} x2={x} y2={CANVAS_H} stroke="rgba(80,140,255,0.25)" strokeWidth={x % (gridSize*3) === 0 ? 0.8 : 0.4} />
              ))}
              {/* Horizontal lines */}
              {Array.from({ length: Math.ceil(CANVAS_H / gridSize) + 1 }, (_, i) => i * gridSize).map(y => (
                <line key={'h'+y} x1={0} y1={y} x2={CANVAS_W} y2={y} stroke="rgba(80,140,255,0.25)" strokeWidth={y % (gridSize*3) === 0 ? 0.8 : 0.4} />
              ))}
              {/* Centre cross */}
              <line x1={CANVAS_W/2} y1={0} x2={CANVAS_W/2} y2={CANVAS_H} stroke="rgba(255,80,80,0.3)" strokeWidth={0.8} strokeDasharray="4 4"/>
              <line x1={0} y1={CANVAS_H/2} x2={CANVAS_W} y2={CANVAS_H/2} stroke="rgba(255,80,80,0.3)" strokeWidth={0.8} strokeDasharray="4 4"/>
            </svg>
          )}

          {/* Layer 1: trace reference image with anchor-based transform */}
          {traceImg && (() => {
            const { x, y, w, h, rot } = traceTransform;
            const cx = x + w/2, cy = y + h/2;
            const HANDLE = 8; // handle size px
            const rotHandleDist = 28; // px above top-centre
            const rotHandleX = cx + Math.sin(rot) * (-rotHandleDist);
            const rotHandleY = cy - Math.cos(rot) * rotHandleDist;
            return (
              <div data-trace-container="1"
                style={{ position:'absolute', inset:0, zIndex:2, pointerEvents:'none' }}>
                {/* The image itself */}
                <img ref={traceImgElRef} src={traceImg} alt="trace reference"
                  onMouseDown={e => onTraceMouseDown(e, 'move')}
                  style={{
                    position:'absolute',
                    left: x, top: y, width: w, height: h,
                    opacity: traceOpacity,
                    transform: `rotate(${rot}rad)`,
                    transformOrigin: 'center',
                    pointerEvents:'all',
                    cursor:'move',
                    userSelect:'none',
                    objectFit: lockAspect ? 'contain' : 'fill',
                    outline:'1px dashed rgba(0,115,234,0.5)',
                  }} />

                {/* Anchor handles SVG overlay */}
                <svg style={{ position:'absolute', inset:0, width:CANVAS_W, height:CANVAS_H, pointerEvents:'none', zIndex:3 }}
                  width={CANVAS_W} height={CANVAS_H}>
                  {/* Rotate handle line */}
                  <line x1={cx} y1={cy - Math.cos(rot)*h/2} x2={rotHandleX} y2={rotHandleY}
                    stroke="rgba(0,115,234,0.7)" strokeWidth={1.5} strokeDasharray="4 2"/>
                  {/* Rotate handle circle */}
                  <circle cx={rotHandleX} cy={rotHandleY} r={HANDLE}
                    fill="#0073ea" opacity={0.85} style={{ pointerEvents:'all', cursor:'grab' }}
                    onMouseDown={e => { e.stopPropagation(); onTraceMouseDown(e, 'rotate'); }} />
                  <text x={rotHandleX} y={rotHandleY+4} textAnchor="middle" fontSize={10} fill="white" style={{ pointerEvents:'none' }}>R</text>
                  {/* Bottom-right resize handle */}
                  <rect x={x + w - HANDLE + w/2 * (1-Math.cos(rot))} y={y + h - HANDLE + h/2 * (1-Math.cos(rot))} width={HANDLE*2} height={HANDLE*2}
                    fill="#0073ea" rx={2} opacity={0.85} style={{ pointerEvents:'all', cursor:'se-resize' }}
                    onMouseDown={e => { e.stopPropagation(); onTraceMouseDown(e, 'resize-br'); }} />
                  {/* Bottom-left resize handle */}
                  <rect x={x - HANDLE} y={y + h - HANDLE} width={HANDLE*2} height={HANDLE*2}
                    fill="#059669" rx={2} opacity={0.85} style={{ pointerEvents:'all', cursor:'sw-resize' }}
                    onMouseDown={e => { e.stopPropagation(); onTraceMouseDown(e, 'resize-bl'); }} />
                  {/* Degree readout */}
                  <text x={cx} y={cy - h/2 - 14} textAnchor="middle" fontSize={9} fill="rgba(0,115,234,0.9)">
                    {Math.round(rot * 180 / Math.PI)}deg
                  </text>
                </svg>
              </div>
            );
          })()}

          {/* Layer 2: strokes only */}
          <canvas ref={drawRef} width={CANVAS_W} height={CANVAS_H}
            style={{ position: 'absolute', inset: 0, zIndex: 3, display: 'block', background: 'transparent' }} />

          {/* Layer 3: live preview + eraser ring cursor */}
          <canvas ref={overlayRef} width={CANVAS_W} height={CANVAS_H}
            style={{ position: 'absolute', inset: 0, zIndex: 4, cursor: cursorMap[tool] || 'crosshair', background: 'transparent' }}
            onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseLeave} />
        </div>
      </div>
    </div>
  );

  return isFullscreen ? (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#323338',
      display: 'flex', flexDirection: 'column',
    }}>
      {canvasPanel}
      {/* Always-visible EXIT button -- overlaid so it's never hidden by the toolbar overflow */}
      <button
        onClick={() => setIsFullscreen(false)}
        title="Exit Fullscreen (Esc)"
        style={{
          position: 'fixed', top: 12, right: 12, zIndex: 10000,
          padding: '8px 14px',
          background: '#e2445c', color: 'white', border: 'none',
          borderRadius: '6px', cursor: 'pointer',
          fontSize: '0.82rem', fontWeight: 'bold',
          display: 'flex', alignItems: 'center', gap: 6,
          boxShadow: '0 2px 12px rgba(0,0,0,0.5)',
        }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/>
          <path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/>
        </svg>
        Exit Fullscreen
      </button>
    </div>
  ) : canvasPanel;
}

// Small toolbar-button style helper
function tbtn(extra = {}) {
  return { padding: '5px 8px', background: 'transparent', border: '1px solid #323338', borderRadius: '6px', cursor: 'pointer', color: '#9699a6', display: 'flex', alignItems: 'center', ...extra };
}

// -------------------------------------------------------------
//  PRINT PROCESS CARD  (unchanged from previous)
// -------------------------------------------------------------
const PrintProcessCard = ({ process, part, db, onClose }) => {
  const smv = calcProcessSMV(process, db);
  const now = new Date().toLocaleString();
  const machines = (process.ops || []).filter(o => o.type === 'machine')
    .map(o => db.machines?.find(m => m.id == o.mcId)?.code).filter(Boolean).join(', ');

  const handlePrint = () => {
    const content = document.getElementById('process-card-print').innerHTML;
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Process Card</title><style>
      body{font-family:Arial,sans-serif;margin:20px;font-size:12px;}
      table{width:100%;border-collapse:collapse;margin:10px 0;}
      th,td{border:1px solid #ddd;padding:6px 10px;}
      th{background:#f0f1f4;font-weight:bold;}
    </style></head><body>${content}</body></html>`);
    w.document.close(); w.print();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '780px', maxHeight: '90vh', overflow: 'auto', padding: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Process Card Preview</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handlePrint} style={{ padding: '8px 16px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', gap: '5px', alignItems: 'center' }}><Printer size={14} /> Print</button>
            <button onClick={onClose} style={{ padding: '8px 16px', background: '#e2445c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={14} /></button>
          </div>
        </div>
        <div id="process-card-print">
          <h2>PROCESS CARD: {process.name}</h2>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', margin: '10px 0', fontSize: '11px', color: '#676879' }}>
            <span>Part: <strong>{part?.name}</strong></span>
            <span>Garment: <strong>{part?.garmentType}</strong></span>
            <span>Fabric: <strong>{part?.fabricType}</strong></span>
            <span>Machines: <strong>{machines || 'Manual'}</strong></span>
          </div>
          <table><thead><tr><th>Type</th><th>Code/Machine</th><th>Length</th><th>Freq</th><th>Stop%</th><th>Fab%</th><th>Direct(s)</th><th>Basic(s)</th></tr></thead>
            <tbody>{(process.ops || []).map((o, i) => {
              const mc = db.machines?.find(m => m.id == o.mcId);
              const mn = db.manualCodes?.find(m => m.id == o.refId);
              return (
                <tr key={i}>
                  <td>{o.type}</td>
                  <td>{o.type === 'machine' ? mc?.code : `${mn?.code} - ${mn?.desc || ''}`}</td>
                  <td style={{ textAlign: 'right' }}>{o.length || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{o.frequency || 1}</td>
                  <td style={{ textAlign: 'right' }}>{o.stoppage || 0}%</td>
                  <td style={{ textAlign: 'right' }}>{o.fabric || 0}%</td>
                  <td style={{ textAlign: 'right' }}>{o.directTime || '-'}</td>
                  <td style={{ textAlign: 'right' }}>{calcBasicSecs(o, db.machines, db.manualCodes).toFixed(3)}</td>
                </tr>
              );
            })}</tbody>
          </table>
          <div style={{ textAlign: 'right', fontSize: '1.3rem', fontWeight: 'bold', color: '#059669', margin: '10px 0' }}>TOTAL SMV: {smv.toFixed(4)} min</div>
          <table><thead><tr><th>Created By</th><th>Created On</th><th>Edited By</th><th>Edited On</th><th>Printed On</th></tr></thead>
            <tbody><tr><td>{process.createdBy || '-'}</td><td>{process.createdOn || '-'}</td><td>{process.editedBy || '-'}</td><td>{process.editedOn || '-'}</td><td>{now}</td></tr></tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  VIDEO MODAL
// -------------------------------------------------------------
const VideoModal = ({ url, onClose }) => {
  const isYoutube = url?.includes('youtube') || url?.includes('youtu.be');
  const embed = isYoutube ? url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/') : url;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001 }}>
      <div style={{ background: '#323338', padding: '15px', borderRadius: '12px', width: '700px', height: '430px', position: 'relative' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: -12, right: -12, background: '#e2445c', color: 'white', borderRadius: '50%', width: 28, height: 28, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={14} /></button>
        {isYoutube ? <iframe src={embed} style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px' }} allowFullScreen /> : <video src={url} controls style={{ width: '100%', height: '100%', borderRadius: '8px' }} />}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  STYLES USED POPUP
// -------------------------------------------------------------
const StylesUsedPopup = ({ part, db, onClose }) => {
  const styles = (db.mgmtStyles || []).filter(s => (s.workstationData?.items || []).some(i => i.id === part.id || i.name === part.name));
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '10px', width: '600px', maxHeight: '80vh', overflow: 'auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
          <h3 style={{ margin: 0 }}>Styles Using: {part.name}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        {styles.length === 0 ? <p style={{ color: '#9699a6', textAlign: 'center' }}>Not used in any style yet.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead><tr style={{ background: '#f0f1f4' }}><th style={{ padding: '8px', textAlign: 'left' }}>Style</th><th style={{ padding: '8px', textAlign: 'left' }}>Season</th><th style={{ padding: '8px', textAlign: 'right' }}>SMV</th><th style={{ padding: '8px', textAlign: 'left' }}>Supplier</th><th style={{ padding: '8px', textAlign: 'left' }}>Used On</th></tr></thead>
            <tbody>{styles.map((s, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '8px' }}>{s.styleRef}</td>
                <td style={{ padding: '8px' }}>{s.season || '-'}</td>
                <td style={{ padding: '8px', textAlign: 'right' }}>{s.workstationData?.smv?.toFixed(3) || '-'}</td>
                <td style={{ padding: '8px' }}>{s.supplier || '-'}</td>
                <td style={{ padding: '8px' }}>{s.createdOn || '-'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  IMAGE SIMILARITY HELPER
// Compares two image src strings by downsampling to 16x16 and
// computing a normalised pixel-distance score (0-100%).
// -------------------------------------------------------------
const computeImageSimilarity = (srcA, srcB) =>
  new Promise((resolve) => {
    if (!srcA || !srcB) return resolve(0);
    const SIZE = 16;
    const toPixels = (src) =>
      new Promise((res) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const c = document.createElement('canvas');
          c.width = SIZE; c.height = SIZE;
          c.getContext('2d').drawImage(img, 0, 0, SIZE, SIZE);
          res(c.getContext('2d').getImageData(0, 0, SIZE, SIZE).data);
        };
        img.onerror = () => res(null);
        img.src = src;
      });

    Promise.all([toPixels(srcA), toPixels(srcB)]).then(([a, b]) => {
      if (!a || !b) return resolve(0);
      let diff = 0;
      for (let i = 0; i < a.length; i += 4) {
        diff += Math.abs(a[i] - b[i]) + Math.abs(a[i+1] - b[i+1]) + Math.abs(a[i+2] - b[i+2]);
      }
      const maxDiff = SIZE * SIZE * 3 * 255;
      resolve(Math.round((1 - diff / maxDiff) * 100));
    });
  });


// ─────────────────────────────────────────────────────────────────────────────
// SMART CLONE MODAL
// Triggered when a new part image is uploaded. Finds visually similar parts,
// lets user compare seam types side-by-side, then clones all data to new part.
// Builds the AI training dataset through repeated use.
// ─────────────────────────────────────────────────────────────────────────────
const SmartCloneModal = ({ newImage, newPartName, allParts, db, onClone, onSkip }) => {
  const [scores, setScores]         = React.useState({});   // { partId: 0-100 }
  const [searching, setSearching]   = React.useState(true);
  const [selected, setSelected]     = React.useState(null); // chosen source part
  const [step, setStep]             = React.useState('pick'); // pick | compare | confirm
  const [overrides, setOverrides]   = React.useState({});   // user edits before clone
  const threshold = 40;

  // Run similarity on mount
  React.useEffect(() => {
    if (!newImage || !allParts.length) { setSearching(false); return; }
    setSearching(true);
    Promise.all(
      allParts.filter(p => p.image).map(p =>
        computeImageSimilarity(newImage, p.image).then(s => [p.id, s])
      )
    ).then(results => {
      setScores(Object.fromEntries(results));
      setSearching(false);
    });
  }, []); // eslint-disable-line

  const similar = allParts
    .map(p => ({ ...p, score: scores[p.id] || 0 }))
    .filter(p => p.score >= threshold)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  const handleClone = () => {
    if (!selected) return;
    const cloned = {
      ...selected,
      ...overrides,
      name:      newPartName || selected.name + ' (Copy)',
      image:     newImage,
      id:        null,
      clonedFrom: selected.id,
      clonedFromName: selected.name,
      clonedOn:  new Date().toISOString(),
      processes: (selected.processes || []).map(pr => ({
        ...pr,
        procCode: null, // will be regenerated
        _cloned: true,
      })),
    };
    onClone(cloned);
  };

  const ov = (field) => overrides[field] !== undefined ? overrides[field] : (selected?.[field] || '');
  const setOv = (field, val) => setOverrides(prev => ({ ...prev, [field]: val }));

  const boxStyle = { background:'white', borderRadius:12, width:'100%', maxWidth:980, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden', boxShadow:'0 24px 64px rgba(0,0,0,0.35)' };
  const hdrStyle = { padding:'14px 20px', background:'#1e1b4b', color:'white', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 };
  const btnP     = (bg='#4f46e5', col='white') => ({ padding:'7px 18px', background:bg, color:col, border:'none', borderRadius:6, cursor:'pointer', fontWeight:700, fontSize:'0.82rem' });

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={boxStyle}>

        {/* Header */}
        <div style={hdrStyle}>
          <div>
            <div style={{ fontWeight:800, fontSize:15 }}>&#128269; Smart Part Clone — Find Similar Parts</div>
            <div style={{ fontSize:11, color:'#a5b4fc', marginTop:2 }}>
              {step === 'pick' ? 'Select the closest matching part to clone its construction data' :
               step === 'compare' ? 'Compare seam types and processes — edit anything before cloning' :
               'Confirm and clone all construction data to new part'}
            </div>
          </div>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <div style={{ fontSize:11, color:'#a5b4fc' }}>Step {step==='pick'?1:step==='compare'?2:3} / 3</div>
            <button onClick={onSkip} style={{ ...btnP('#312e81'), fontSize:12 }}>Skip &rarr; Start Fresh</button>
          </div>
        </div>

        {/* Steps indicator */}
        <div style={{ display:'flex', background:'#f5f3ff', borderBottom:'1px solid #e0e7ff', flexShrink:0 }}>
          {[['pick','1. Pick Similar Part'],['compare','2. Compare &amp; Edit'],['confirm','3. Confirm Clone']].map(([s,label]) => (
            <div key={s} style={{ padding:'8px 20px', fontSize:12, fontWeight: step===s?700:400, color: step===s?'#4f46e5':'#94a3b8', borderBottom: step===s?'2px solid #4f46e5':'2px solid transparent', cursor: selected && s!=='pick' ? 'pointer' : 'default' }}
              onClick={() => { if (selected && s !== 'pick') setStep(s); }}
              dangerouslySetInnerHTML={{ __html: label }}/>
          ))}
        </div>

        <div style={{ flex:1, display:'flex', minHeight:0 }}>

          {/* ── STEP 1: Pick ── */}
          {step === 'pick' && (
            <div style={{ flex:1, overflowY:'auto', padding:20 }}>
              {searching ? (
                <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>&#128269;</div>
                  <div style={{ fontWeight:600 }}>Scanning {allParts.filter(p=>p.image).length} parts for visual similarity...</div>
                </div>
              ) : similar.length === 0 ? (
                <div style={{ textAlign:'center', padding:40, color:'#64748b' }}>
                  <div style={{ fontSize:32, marginBottom:12 }}>&#128100;</div>
                  <div style={{ fontWeight:600, marginBottom:8 }}>No similar parts found above {threshold}% match</div>
                  <div style={{ fontSize:13 }}>This appears to be a unique part. Start fresh or lower the threshold.</div>
                  <button style={{ ...btnP(), marginTop:16 }} onClick={onSkip}>Start Fresh Part</button>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom:14, fontSize:13, color:'#64748b' }}>Found <strong>{similar.length}</strong> similar part{similar.length!==1?'s':''} — select the closest match:</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px,1fr))', gap:12 }}>
                    {/* New part card */}
                    <div style={{ border:'2px solid #4f46e5', borderRadius:10, padding:12, background:'#f5f3ff' }}>
                      <div style={{ fontSize:10, fontWeight:700, color:'#4f46e5', marginBottom:6, textTransform:'uppercase' }}>New Part</div>
                      <img src={newImage} alt="new" style={{ width:'100%', height:120, objectFit:'contain', border:'1px solid #e0e7ff', borderRadius:6, background:'white' }} />
                      <div style={{ marginTop:6, fontWeight:700, fontSize:13 }}>{newPartName || 'Unnamed Part'}</div>
                    </div>
                    {/* Similar parts */}
                    {similar.map(p => (
                      <div key={p.id} onClick={() => setSelected(p)}
                        style={{ border:`2px solid ${selected?.id===p.id?'#4f46e5':'#e2e8f0'}`, borderRadius:10, padding:12, cursor:'pointer', background: selected?.id===p.id?'#eff6ff':'white', transition:'all 0.15s' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                          <span style={{ fontSize:10, fontWeight:700, color:'#64748b', textTransform:'uppercase' }}>Match</span>
                          <span style={{ background: p.score>=80?'#dcfce7':p.score>=60?'#fef3c7':'#fee2e2', color: p.score>=80?'#166534':p.score>=60?'#92400e':'#991b1b', padding:'1px 8px', borderRadius:10, fontSize:11, fontWeight:700 }}>{p.score}%</span>
                        </div>
                        <img src={p.image} alt={p.name} style={{ width:'100%', height:120, objectFit:'contain', border:'1px solid #e2e8f0', borderRadius:6, background:'#f8fafc' }} />
                        <div style={{ marginTop:6, fontWeight:700, fontSize:13, color:'#1e293b' }}>{p.name}</div>
                        <div style={{ fontSize:11, color:'#64748b', marginTop:2 }}>{p.garmentType} · {p.fabricType || 'Any fabric'}</div>
                        <div style={{ fontSize:11, color:'#4f46e5', marginTop:2 }}>{(p.processes||[]).length} processes · SMV ~{(p.processes||[]).reduce((s,pr)=>s+(parseFloat(pr.smvOverride)||0),0).toFixed(3)}</div>
                        {selected?.id===p.id && <div style={{ marginTop:6, textAlign:'center', color:'#4f46e5', fontWeight:700, fontSize:11 }}>&#10003; Selected</div>}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── STEP 2: Compare & Edit ── */}
          {step === 'compare' && selected && (
            <div style={{ flex:1, overflowY:'auto', padding:20 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20, marginBottom:20 }}>
                {/* Source */}
                <div style={{ border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' }}>
                  <div style={{ padding:'10px 14px', background:'#f8fafc', fontWeight:700, fontSize:13, color:'#64748b', borderBottom:'1px solid #e2e8f0' }}>
                    Source: {selected.name} ({scores[selected.id]}% match)
                  </div>
                  <div style={{ padding:14 }}>
                    <img src={selected.image} alt={selected.name} style={{ width:'100%', height:140, objectFit:'contain', background:'#f8fafc', borderRadius:6, border:'1px solid #e2e8f0', marginBottom:10 }} />
                    {(selected.processes||[]).map((pr,i) => {
                      const sd = pr.seamDiagramId ? (db.seamDiagrams||[]).find(s=>String(s.id)===String(pr.seamDiagramId)) : null;
                      return (
                        <div key={i} style={{ padding:'6px 8px', borderBottom:'1px solid #f1f5f9', fontSize:12 }}>
                          <span style={{ fontFamily:'monospace', color:'#4f46e5', marginRight:6, fontSize:10 }}>{pr.procCode||`P${String(i+1).padStart(3,'0')}`}</span>
                          <span style={{ fontWeight:600 }}>{pr.name}</span>
                          {sd && <span style={{ marginLeft:6, background:'#e0e7ff', color:'#4338ca', padding:'1px 5px', borderRadius:4, fontSize:10 }}>{sd.code||sd.name}</span>}
                          {pr.seamDiagramNote && <span style={{ marginLeft:4, color:'#64748b', fontSize:10 }}>— {pr.seamDiagramNote}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* New part editable */}
                <div style={{ border:'2px solid #4f46e5', borderRadius:10, overflow:'hidden' }}>
                  <div style={{ padding:'10px 14px', background:'#f5f3ff', fontWeight:700, fontSize:13, color:'#4f46e5', borderBottom:'1px solid #e0e7ff' }}>
                    New Part — Edit Before Cloning
                  </div>
                  <div style={{ padding:14 }}>
                    <img src={newImage} alt="new" style={{ width:'100%', height:140, objectFit:'contain', background:'#f8fafc', borderRadius:6, border:'1px solid #e0e7ff', marginBottom:10 }} />
                    {/* Editable fields */}
                    {[['garmentType','Garment Type'],['fabricType','Fabric Type'],['designAttr','Design / Attr']].map(([field,label]) => (
                      <div key={field} style={{ marginBottom:8 }}>
                        <div style={{ fontSize:10, color:'#64748b', fontWeight:600, marginBottom:2 }}>{label}</div>
                        <input value={ov(field)} onChange={e=>setOv(field,e.target.value)}
                          style={{ width:'100%', padding:'5px 8px', border:'1px solid #c7d2fe', borderRadius:5, fontSize:12, boxSizing:'border-box' }} />
                      </div>
                    ))}
                    <div style={{ marginTop:10, padding:'8px 10px', background:'#eff6ff', borderRadius:6, fontSize:11, color:'#3b82f6' }}>
                      {(selected.processes||[]).length} processes will be cloned. You can edit individual processes after saving.
                    </div>
                    {/* Seam types comparison */}
                    <div style={{ marginTop:10 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#4f46e5', marginBottom:6 }}>Seam Types in this part:</div>
                      {[...new Set((selected.processes||[]).map(pr=>pr.seamDiagramId).filter(Boolean))].map(sdId => {
                        const sd = (db.seamDiagrams||[]).find(s=>String(s.id)===String(sdId));
                        if (!sd) return null;
                        return (
                          <div key={sdId} style={{ display:'flex', gap:8, alignItems:'center', padding:'4px 6px', background:'white', borderRadius:5, border:'1px solid #e0e7ff', marginBottom:4 }}>
                            {sd.image && <img src={sd.image} alt={sd.name} style={{ height:32, width:32, objectFit:'contain', borderRadius:3 }} />}
                            <div>
                              <div style={{ fontWeight:600, fontSize:11 }}>{sd.name} {sd.code ? `(${sd.code})` : ''}</div>
                              {sd.machineHint && <div style={{ fontSize:10, color:'#059669' }}>Machine: {sd.machineHint}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Confirm ── */}
          {step === 'confirm' && selected && (
            <div style={{ flex:1, overflowY:'auto', padding:20 }}>
              <div style={{ maxWidth:560, margin:'0 auto', textAlign:'center' }}>
                <div style={{ fontSize:48, marginBottom:12 }}>&#10004;</div>
                <div style={{ fontWeight:800, fontSize:18, color:'#1e293b', marginBottom:8 }}>Ready to Clone</div>
                <div style={{ fontSize:13, color:'#64748b', marginBottom:24 }}>
                  All construction data from <strong>{selected.name}</strong> will be copied to <strong>{newPartName||'New Part'}</strong>.
                  Processes, seam types, machine codes, and SMV values are all included.
                  You can edit any detail after saving.
                </div>
                <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:16, textAlign:'left', marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'#64748b', marginBottom:8 }}>WHAT WILL BE CLONED:</div>
                  {[
                    `${(selected.processes||[]).length} processes with codes`,
                    `${[...new Set((selected.processes||[]).map(p=>p.seamDiagramId).filter(Boolean))].length} seam diagram types`,
                    'Machine and manual codes per process',
                    'Allowances and fabric difficulty settings',
                    'Process purposes and attachments',
                  ].map((item,i) => <div key={i} style={{ fontSize:12, color:'#475569', padding:'3px 0' }}>&#10003; {item}</div>)}
                </div>
                <div style={{ fontSize:11, color:'#94a3b8', marginBottom:16 }}>
                  This data will contribute to AI training for automated SMV prediction and part recognition.
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, background:'#f8fafc' }}>
          <button onClick={onSkip} style={btnP('#f1f5f9','#475569')}>Skip &mdash; Start Fresh</button>
          <div style={{ display:'flex', gap:8 }}>
            {step !== 'pick' && <button onClick={() => setStep(step==='confirm'?'compare':'pick')} style={btnP('#f1f5f9','#475569')}>&larr; Back</button>}
            {step === 'pick' && <button onClick={() => { if (!selected) return alert('Select a similar part first'); setStep('compare'); }} style={btnP()}>&rarr; Compare &amp; Edit</button>}
            {step === 'compare' && <button onClick={() => setStep('confirm')} style={btnP()}>Looks Good &rarr; Confirm</button>}
            {step === 'confirm' && <button onClick={handleClone} style={btnP('#059669')}>&#10004; Clone to New Part</button>}
          </div>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  PART GALLERY
// -------------------------------------------------------------
const PartGallery = ({ db, onView, onEdit, onDelete, onCreateNew, onClone }) => {
  const [filters, setFilters]         = useState({ fabric: '', garment: '', design: '' });
  const [textSearch, setTextSearch]   = useState('');
  const [imgSearchSrc, setImgSearchSrc] = useState(null);   // base64 of uploaded query image
  const [imgScores, setImgScores]     = useState({});       // { partId: score 0-100 }
  const [imgSearching, setImgSearching] = useState(false);
  const [imgThreshold, setImgThreshold] = useState(60);     // min % to show
  const [showImgSearch, setShowImgSearch] = useState(false);
  const [hovered, setHovered]         = useState(null);
  const [stylesPopup, setStylesPopup] = useState(null);

  const parts = db.masterParts || [];
  const uniq  = (key) => [...new Set(parts.map(p => p[key]).filter(Boolean))];

  // Run image similarity when query image changes
  useEffect(() => {
    if (!imgSearchSrc) { setImgScores({}); return; }
    setImgSearching(true);
    Promise.all(
      parts.map(p =>
        p.image
          ? computeImageSimilarity(imgSearchSrc, p.image).then(score => [p.id, score])
          : Promise.resolve([p.id, 0])
      )
    ).then(results => {
      setImgScores(Object.fromEntries(results));
      setImgSearching(false);
    });
  }, [imgSearchSrc, parts.length]); // eslint-disable-line

  const filtered = parts.filter(p => {
    const s = textSearch.toLowerCase();
    const textMatch =
      (!filters.fabric  || (p.fabricType  || '').toLowerCase().includes(filters.fabric.toLowerCase())) &&
      (!filters.garment || (p.garmentType || '').toLowerCase().includes(filters.garment.toLowerCase())) &&
      (!filters.design  || (p.designAttr  || '').toLowerCase().includes(filters.design.toLowerCase())) &&
      (!s || (p.name || '').toLowerCase().includes(s));
    if (!textMatch) return false;
    if (imgSearchSrc) return (imgScores[p.id] || 0) >= imgThreshold;
    return true;
  });

  // Sort by image score descending when image search active
  const sorted = imgSearchSrc
    ? [...filtered].sort((a, b) => (imgScores[b.id] || 0) - (imgScores[a.id] || 0))
    : filtered;

  const handleDelete = (part) => {
    const used = (db.mgmtStyles || []).filter(s =>
      (s.workstationData?.items || []).some(i => i.id === part.id || i.name === part.name));
    if (used.length > 0) return alert(`Cannot delete "${part.name}" - used in ${used.length} style(s).`);
    if (window.confirm(`Delete part "${part.name}"?`)) onDelete(part.id);
  };

  const handleImgUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setImgSearchSrc(ev.target.result);
    reader.readAsDataURL(file);
  };

  const scoreColor = (s) => s >= 80 ? '#059669' : s >= 60 ? '#fdab3d' : '#9699a6';

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* -- Single compact filter bar -- all controls fit on one line -- */}
      <div style={{ background: 'white', padding: '6px 12px', borderBottom: '1px solid #e6e9ef', display: 'flex', gap: '5px', alignItems: 'center', flexShrink: 0 }}>

        {/* Text search -- same flex weight as filters */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: '#f0f1f4', borderRadius: '6px', padding: '4px 8px', flex: '1 1 0', minWidth: 0, maxWidth: '150px' }}>
          <Search size={12} color="#676879" style={{ flexShrink: 0 }} />
          <input value={textSearch} onChange={e => setTextSearch(e.target.value)}
            placeholder="Search..."
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '0.8rem', width: '100%', minWidth: 0 }} />
        </div>

        {/* Fabric filter -- from db.fabricDifficulties / db.fabrics (Global Masters) + existing parts */}
        <select value={filters.fabric} onChange={e => setFilters({ ...filters, fabric: e.target.value })}
          style={{ padding: '4px 6px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.78rem', flex: '1 1 0', minWidth: 0, maxWidth: '130px' }}>
          <option value="">Fabric</option>
          {[...new Set([
            ...(db.fabricDifficulties || db.fabrics || []).map(f => f.fabric || f.name || '').filter(Boolean),
            ...uniq('fabricType')
          ])].map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        {/* Garment filter -- from db.products (Global Masters) + existing parts */}
        <select value={filters.garment} onChange={e => setFilters({ ...filters, garment: e.target.value })}
          style={{ padding: '4px 6px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.78rem', flex: '1 1 0', minWidth: 0, maxWidth: '130px' }}>
          <option value="">Garment</option>
          {[...new Set([
            ...(db.products || []).map(p => p.name || p).filter(Boolean),
            ...uniq('garmentType')
          ])].map(o => <option key={o} value={o}>{o}</option>)}
        </select>

        {/* Design filter */}
        <input value={filters.design} onChange={e => setFilters({ ...filters, design: e.target.value })}
          placeholder="Design..."
          style={{ padding: '4px 6px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.78rem', flex: '1 1 0', minWidth: 0, maxWidth: '110px' }} />

        {/* Image search toggle */}
        <button onClick={() => { setShowImgSearch(s => !s); if (showImgSearch) { setImgSearchSrc(null); setImgScores({}); } }}
          title="Search by image similarity"
          style={{ padding: '4px 8px', border: '1px solid', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0, whiteSpace: 'nowrap',
            background: showImgSearch ? '#e8f0ff' : 'white', borderColor: showImgSearch ? '#93c5fd' : '#e6e9ef', color: showImgSearch ? '#0073ea' : '#676879' }}>
          <ImageIcon size={12} /> Img
        </button>

        {/* Image search controls - shown inline when active */}
        {showImgSearch && (
          <>
            <label style={{ padding: '4px 8px', background: imgSearchSrc ? '#e3faf0' : '#f0f1f4', border: '1px solid', borderColor: imgSearchSrc ? '#86efac' : '#e6e9ef', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0, whiteSpace: 'nowrap', color: imgSearchSrc ? '#166534' : '#676879' }}>
              {imgSearching ? 'R' : imgSearchSrc ? '(/) Img' : '+ Img'}
              <input type="file" accept="image/*" hidden onChange={handleImgUpload} />
            </label>
            {imgSearchSrc && (
              <>
                <img src={imgSearchSrc} alt="query" style={{ height: '26px', width: '26px', objectFit: 'cover', borderRadius: '6px', border: '1px solid #e6e9ef', flexShrink: 0 }} />
                <input type="range" min={20} max={95} value={imgThreshold} onChange={e => setImgThreshold(+e.target.value)}
                  title={`Min match: ${imgThreshold}%`} style={{ width: '60px', flexShrink: 0 }} />
                <span style={{ fontSize: '0.72rem', color: '#676879', flexShrink: 0 }}>{imgThreshold}%</span>
                <button onClick={() => { setImgSearchSrc(null); setImgScores({}); }}
                  style={{ padding: '3px 5px', background: 'none', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#e2445c', flexShrink: 0 }}>
                  <X size={11} />
                </button>
              </>
            )}
          </>
        )}

        {/* NEW PART -- fixed width, never shrinks */}
        <button onClick={onCreateNew}
          style={{ padding: '5px 12px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 'bold', fontSize: '0.78rem', flexShrink: 0, whiteSpace: 'nowrap' }}>
          <Plus size={12} /> NEW
        </button>
      </div>

      {/* -- Results summary when image search active -- */}
      {imgSearchSrc && !imgSearching && (
        <div style={{ background: '#e8f0ff', borderBottom: '1px solid #a8d1ff', padding: '6px 16px', fontSize: '0.78rem', color: '#1e40af', display: 'flex', gap: '16px', alignItems: 'center' }}>
          <span>Image search active -- showing {sorted.length} part{sorted.length !== 1 ? 's' : ''} with &gt;={imgThreshold}% match</span>
          <span style={{ color: '#676879' }}>Sorted by similarity score</span>
        </div>
      )}

      {/* -- Grid -- */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: '#f5f6f8' }}>
        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9699a6', paddingTop: '60px' }}>
            <Grid size={40} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.3 }} />
            <p>{imgSearchSrc ? `No parts matched >=${imgThreshold}% -- try lowering the threshold` : 'No parts found.'}</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '14px' }}>
            {sorted.map(p => {
              const smv = totalPartSMV(p.processes, db);
              const stylesCount = (db.mgmtStyles || []).filter(s =>
                (s.workstationData?.items || []).some(i => i.id === p.id || i.name === p.name)).length;
              const score = imgScores[p.id];

              return (
                <div key={p.id}
                  style={{ background: 'white', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e6e9ef', transition: 'box-shadow 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.13)'; setHovered(p.id); }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.08)'; setHovered(null); }}>

                  {/* Thumbnail */}
                  <div style={{ height: '130px', background: '#f0f1f4', position: 'relative', overflow: 'hidden' }}>
                    {p.image
                      ? <img src={p.image} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt={p.name} />
                      : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><Grid size={28} color="#c5c7d4" /></div>}

                    {/* Image similarity badge */}
                    {imgSearchSrc && score !== undefined && (
                      <div style={{ position: 'absolute', top: 5, left: 5, background: scoreColor(score), color: 'white', fontSize: '0.68rem', fontWeight: 'bold', padding: '2px 7px', borderRadius: '10px' }}>
                        {score}% match
                      </div>
                    )}

                    {/* Hover overlay */}
                    {hovered === p.id && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.82)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'white', fontSize: '0.75rem', gap: '4px', padding: '10px', textAlign: 'center' }}>
                        <div><strong>{p.name}</strong></div>
                        <div>Fabric: {p.fabricType || '-'}</div>
                        <div>Design: {p.designAttr || '-'}</div>
                        <div>SMV: <strong style={{ color: '#4ade80' }}>{smv.toFixed(3)}</strong> min</div>
                        <div>{p.processes?.length || 0} proc \u00B7 {stylesCount} style(s)</div>
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div style={{ padding: '9px 11px' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '0.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '2px' }}>{p.name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#676879', marginBottom: '5px' }}>
                      <span>{p.designAttr || 'No design'}</span>
                      <span style={{ color: '#059669', fontWeight: 'bold' }}>{smv.toFixed(3)} min</span>
                    </div>
                    <div style={{ fontSize: '0.68rem', color: '#9699a6', marginBottom: '7px' }}>
                      By: {p.createdBy || 'N/A'} \u00B7 {p.createdOn || 'N/A'}
                      {p.approvedBy && <span> \u00B7 Appr: {p.approvedBy}</span>}
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => setStylesPopup(p)}
                        style={{ flex: 1, padding: '4px', fontSize: '0.68rem', background: '#f0f1f4', border: '1px solid #e6e9ef', borderRadius: '6px', cursor: 'pointer', color: '#676879', whiteSpace: 'nowrap' }}>
                        {stylesCount} Style{stylesCount !== 1 ? 's' : ''}
                      </button>
                      <button onClick={() => onView(p)} title="View"
                        style={{ padding: '4px 7px', background: '#e8f0ff', border: '1px solid #a8d1ff', borderRadius: '6px', cursor: 'pointer', color: '#0073ea', display: 'flex', alignItems: 'center' }}><Eye size={12} /></button>
                      <button onClick={() => onEdit(p)} title="Edit"
                        style={{ padding: '4px 7px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: '6px', cursor: 'pointer', color: '#fdab3d', display: 'flex', alignItems: 'center' }}><Edit2 size={12} /></button>
                      <button onClick={() => onClone(p)} title="Clone as new part"
                        style={{ padding: '4px 7px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', cursor: 'pointer', color: '#00a65e', display: 'flex', alignItems: 'center' }}><Copy size={12} /></button>
                      <button onClick={() => handleDelete(p)} title="Delete"
                        style={{ padding: '4px 7px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: 'pointer', color: '#e2445c', display: 'flex', alignItems: 'center' }}><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {stylesPopup && <StylesUsedPopup part={stylesPopup} db={db} onClose={() => setStylesPopup(null)} />}
    </div>
  );
};

// -------------------------------------------------------------
//  PART DETAIL VIEW
// -------------------------------------------------------------
const PartDetailView = ({ part, db, onClose, onEdit }) => {
  const [videoModal, setVideoModal] = useState(null);
  const smv = totalPartSMV(part.processes, db);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 900, overflowY: 'auto' }}>
      <div style={{ background: 'white', borderRadius: '12px', width: '860px', maxHeight: '92vh', overflow: 'auto', margin: '20px' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e6e9ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: 'white', zIndex: 10 }}>
          <div>
            <h2 style={{ margin: 0 }}>{part.name}</h2>
            <div style={{ fontSize: '0.8rem', color: '#676879', marginTop: '2px' }}>{part.garmentType} \u00B7 {part.fabricType} \u00B7 {part.designAttr}</div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => onEdit(part)} style={{ padding: '8px 14px', background: '#fdab3d', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', gap: '5px', alignItems: 'center' }}><Edit2 size={14} /> Edit</button>
            <button onClick={onClose} style={{ padding: '8px 14px', background: '#e2445c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={14} /></button>
          </div>
        </div>
        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '240px 1fr', gap: '24px' }}>
          <div>
            <div style={{ height: '220px', background: '#f0f1f4', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', marginBottom: '12px' }}>
              {part.image ? <img src={part.image} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt={part.name} /> : <Grid size={32} color="#c5c7d4" />}
            </div>
            <div style={{ background: '#f5f6f8', border: '1px solid #e6e9ef', borderRadius: '8px', padding: '12px', fontSize: '0.82rem' }}>
              {[['Total SMV', `${smv.toFixed(4)} min`, '#059669'], ['Processes', part.processes?.length || 0, null], ['Created By', part.createdBy || '-', null], ['Created On', part.createdOn || '-', null], ['Approved By', part.approvedBy || '-', null]].map(([label, val, col]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#676879' }}>{label}</span>
                  <strong style={{ color: col || 'inherit' }}>{val}</strong>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 style={{ margin: '0 0 12px', color: '#323338' }}>PROCESSES & SMV BREAKDOWN</h4>
            {(part.processes || []).length === 0 ? <p style={{ color: '#9699a6' }}>No processes defined.</p> : (part.processes || []).map((pr, pi) => {
              const psmv = calcProcessSMV(pr, db);
              return (
                <div key={pi} style={{ border: '1px solid #e6e9ef', borderRadius: '8px', marginBottom: '12px', overflow: 'hidden' }}>
                  <div style={{ background: '#f5f6f8', padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <strong style={{ fontSize: '0.88rem' }}>{pr.name}</strong>
                      {pr.video && <button onClick={() => setVideoModal(pr.video)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073ea', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.75rem' }}><PlayCircle size={14} /> Video</button>}
                    </div>
                    <span style={{ color: '#059669', fontWeight: 'bold' }}>{psmv.toFixed(4)} min</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {videoModal && <VideoModal url={videoModal} onClose={() => setVideoModal(null)} />}
    </div>
  );
};

// -------------------------------------------------------------
//  OPERATION BUILDER  (unchanged from previous version)
// -------------------------------------------------------------
const OpBuilderPanel = ({ proc, setProc, db }) => {
  const [opType, setOpType] = useState('machine');
  const [cur, setCur] = useState({ mcId: '', length: '', stoppage: 0, fabric: 0, refId: '', frequency: 1, directTime: '' });
  const [staged, setStaged] = useState([]);

  const getLabel = (o) => {
    if (o.type === 'machine') return db.machines?.find(m => m.id == o.mcId)?.code || 'Unknown';
    const mn = db.manualCodes?.find(m => m.id == o.refId);
    return mn ? `${mn.code} - ${mn.desc}` : 'Unknown';
  };

  const handleStage = () => {
    if (opType === 'machine' && !cur.mcId) return alert('Select Machine');
    if (opType === 'manual' && !cur.refId) return alert('Select Manual Code');
    setStaged(s => [...s, { type: opType, ...cur, id: Date.now() }]);
    setCur({ mcId: '', length: '', stoppage: 0, fabric: 0, refId: '', frequency: 1, directTime: '' });
  };

  const commit = () => {
    if (!staged.length) return alert('No staged ops');
    setProc(p => ({ ...p, ops: [...p.ops, ...staged] }));
    setStaged([]);
  };

  return (
    <div style={{ flex: 1, background: '#f5f6f8', padding: '12px', borderRadius: '8px', border: '1px solid #e6e9ef', display: 'flex', flexDirection: 'column', gap: '10px', overflowY: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
        <input placeholder="Process Name *" value={proc.name} onChange={e => setProc({ ...proc, name: e.target.value })} style={{ padding: '7px', border: `1px solid ${proc.name ? '#e6e9ef' : '#fca5a5'}`, borderRadius: '5px' }} />
        <input placeholder="Guide / Attachment" value={proc.attachment} onChange={e => setProc({ ...proc, attachment: e.target.value })} style={{ padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px' }} />
        <input placeholder="Video URL / YouTube" value={proc.video} onChange={e => setProc({ ...proc, video: e.target.value })} style={{ padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px' }} />
      </div>
      {/* Purpose / description for this process */}
      <input placeholder="Purpose of process e.g. Pattern placed over front -- L6 -- L6 generic description"
        value={proc.purpose || ''}
        onChange={e => setProc({ ...proc, purpose: e.target.value })}
        style={{ padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px', fontSize:'0.8rem', color:'#676879', width:'100%', boxSizing:'border-box' }} />

      {/* ── Seam Diagram ── */}
      <div style={{ background: '#f0f4ff', border: '1px solid #c7d5f8', borderRadius: '7px', padding: '10px' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#3b4bb3', marginBottom: '7px', display:'flex', alignItems:'center', gap:6 }}>
          SEAM DIAGRAM
          <span style={{ fontSize:'0.62rem', background:'#e0e7ff', color:'#4338ca', padding:'1px 6px', borderRadius:8, fontWeight:500 }}>AI Training Data</span>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
          {/* Select from library */}
          <select value={proc.seamDiagramId || ''} onChange={e => setProc({ ...proc, seamDiagramId: e.target.value })}
            style={{ padding:'6px', border:'1px solid #c7d5f8', borderRadius:'5px', fontSize:'0.8rem', background:'white' }}>
            <option value="">-- Select Seam Type --</option>
            {(db.seamDiagrams || []).map(s => (
              <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</option>
            ))}
            {(db.seamDiagrams || []).length === 0 && <option disabled>Add seam diagrams in Tech Masters</option>}
          </select>
          {/* Inline note */}
          <input placeholder="Seam note (e.g. 1cm seam, flat-felled)" value={proc.seamDiagramNote || ''}
            onChange={e => setProc({ ...proc, seamDiagramNote: e.target.value })}
            style={{ padding:'6px', border:'1px solid #c7d5f8', borderRadius:'5px', fontSize:'0.8rem' }} />
        </div>
        {/* Show selected seam diagram image */}
        {proc.seamDiagramId && (() => {
          const sd = (db.seamDiagrams || []).find(s => String(s.id) === String(proc.seamDiagramId));
          return sd?.image ? (
            <div style={{ marginTop:8, display:'flex', alignItems:'flex-start', gap:10 }}>
              <img src={sd.image} alt={sd.name} style={{ height:60, objectFit:'contain', border:'1px solid #c7d5f8', borderRadius:5, background:'white', padding:4 }} />
              <div>
                <div style={{ fontWeight:700, fontSize:'0.78rem', color:'#3b4bb3' }}>{sd.name}</div>
                {sd.description && <div style={{ fontSize:'0.72rem', color:'#676879', marginTop:2 }}>{sd.description}</div>}
                {sd.machineHint && <div style={{ fontSize:'0.7rem', color:'#059669', marginTop:2 }}>Machine hint: {sd.machineHint}</div>}
              </div>
            </div>
          ) : null;
        })()}
      </div>

      <div style={{ background: 'white', padding: '10px', border: '1px solid #e6e9ef', borderRadius: '7px' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#676879', marginBottom: '7px' }}>ADD CODE</div>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '7px' }}>
          <label style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer', fontSize: '0.82rem' }}><input type="radio" checked={opType === 'machine'} onChange={() => setOpType('machine')} /> Machine</label>
          <label style={{ display: 'flex', gap: '4px', alignItems: 'center', cursor: 'pointer', fontSize: '0.82rem' }}><input type="radio" checked={opType === 'manual'} onChange={() => setOpType('manual')} /> Manual</label>
        </div>
        {opType === 'machine' ? (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr', gap: '5px' }}>
            <select value={cur.mcId} onChange={e => setCur({ ...cur, mcId: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }}>
              <option value="">Machine...</option>
                {(db.machines || []).map(m => <option key={m.id} value={m.id}>{m.code}{m.name ? ` - ${m.name}` : ''}</option>)}
                {(db.machines || []).length === 0 && <option disabled>Add machines in Admin -&gt; Tech Masters</option>}
            </select>
            <input type="number" placeholder="Length" value={cur.length} onChange={e => setCur({ ...cur, length: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }} />
            <select value={cur.stoppage} onChange={e => setCur({ ...cur, stoppage: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }}>
              <option value="0">Stop...</option>
                {(db.stoppages || []).map(s => <option key={s.id} value={s.factor}>{s.code} ({s.factor}%)</option>)}
                {(db.stoppages || []).length === 0 && <option disabled>Add stoppages in Tech Masters</option>}
            </select>
            <select value={cur.fabric} onChange={e => setCur({ ...cur, fabric: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }}>
              <option value="0">Fab...</option>
                {(db.fabricDifficulties || db.fabrics || []).map((f,i) => {
                  const name = f.fabric || f.name || '';
                  const fac  = parseFloat(f.factor || f.difficultyFactor || 0);
                  return <option key={f.id||i} value={fac}>{name}{fac > 0 ? ` (${fac}%)` : ''}</option>;
                })}
                {(db.fabricDifficulties || db.fabrics || []).length === 0 && <option disabled>Add fabrics in Global Masters</option>}
            </select>
            <input type="number" placeholder="Direct(s)" value={cur.directTime} onChange={e => setCur({ ...cur, directTime: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }} />
            <input type="number" placeholder="Freq" min="1" value={cur.frequency} onChange={e => setCur({ ...cur, frequency: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }} />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '5px' }}>
            <select value={cur.refId} onChange={e => setCur({ ...cur, refId: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }}>
              <option value="">Manual Code...</option>
                {(db.manualCodes || []).map(m => <option key={m.id} value={m.id}>{m.code}{m.name ? ` - ${m.name}` : m.desc ? ` - ${m.desc}` : ''}</option>)}
                {(db.manualCodes || []).length === 0 && <option disabled>Add manual codes in Admin -&gt; Tech Masters</option>}
            </select>
            <input type="number" placeholder="Freq" min="1" value={cur.frequency} onChange={e => setCur({ ...cur, frequency: e.target.value })} style={{ padding: '5px', border: '1px solid #e6e9ef', borderRadius: '6px', fontSize: '0.8rem' }} />
          </div>
        )}
        {/* Allowances from Tech Masters */}
        {(db.allowances || []).length > 0 && (
          <div style={{ marginTop: '6px', display: 'flex', gap: '5px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.72rem', color: '#676879', flexShrink: 0 }}>Allowance:</span>
            <select onChange={e => {
              if (!e.target.value) return;
              const al = (db.allowances || []).find(a => String(a.id) === e.target.value);
              if (al) setCur(c => ({ ...c, allowance: parseFloat(al.factor || al.value || 0), allowanceCode: al.code }));
            }} style={{ flex: 1, padding: '4px', border: '1px solid #e6e9ef', borderRadius: '5px', fontSize: '0.78rem' }}>
              <option value="">+ Add Allowance...</option>
              {(db.allowances || []).map(a => <option key={a.id} value={a.id}>{a.code} ({a.factor || a.value}%)</option>)}
            </select>
            {cur.allowanceCode && <span style={{ fontSize: '0.72rem', background: '#e8f5e9', color: '#166534', padding: '2px 8px', borderRadius: '10px' }}>{cur.allowanceCode}: {cur.allowance}%</span>}
          </div>
        )}
        <button onClick={handleStage} style={{ marginTop: '6px', width: '100%', padding: '6px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem' }}>+ Stage Code</button>
      </div>

      {staged.length > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '7px', padding: '8px' }}>
          <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#92400e', marginBottom: '5px' }}>STAGED - REVIEW BEFORE COMMIT</div>
          {staged.map(o => (
            <div key={o.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '3px', fontSize: '0.78rem' }}>
              <span style={{ flex: 1 }}>{getLabel(o)}</span>
              <input type="number" min="1" value={o.frequency || 1} onChange={e => setStaged(s => s.map(x => x.id === o.id ? { ...x, frequency: e.target.value } : x))} style={{ width: '48px', padding: '2px', border: '1px solid #fcd34d', borderRadius: '3px', textAlign: 'right', fontSize: '0.78rem' }} />
              <span style={{ minWidth: '50px', textAlign: 'right', color: '#676879' }}>{calcBasicSecs(o, db.machines, db.manualCodes).toFixed(2)}s</span>
              <button onClick={() => setStaged(s => s.filter(x => x.id !== o.id))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e2445c' }}><X size={11} /></button>
            </div>
          ))}
          <button onClick={commit} style={{ marginTop: '5px', width: '100%', padding: '6px', background: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 'bold' }}>(/) Add to Process</button>
        </div>
      )}

      {proc.ops.length > 0 && (
        <div>
          <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#676879', marginBottom: '5px' }}>COMMITTED OPS</div>
          {proc.ops.map(o => (
            <div key={o.id} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid #f0f1f4', fontSize: '0.78rem' }}>
              <span style={{ fontSize:'0.65rem', color:'#9699a6', fontFamily:'monospace', flexShrink:0 }}>{o.type==='machine'?'M':'H'}</span>
              <span style={{ flex: 1 }}>{getLabel(o)}</span>
              <span style={{ color: '#676879' }}>x{o.frequency || 1}</span>
              <span style={{ minWidth: '50px', textAlign: 'right', color: '#059669', fontWeight: 'bold' }}>{calcBasicSecs(o, db.machines, db.manualCodes).toFixed(2)}s</span>
              <button onClick={() => setProc(p => ({ ...p, ops: p.ops.filter(x => x.id !== o.id) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e2445c' }}><X size={11} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------
//  SHARE MODAL
// -------------------------------------------------------------
const ShareModal = ({ part, db, onClose }) => {
  const [to, setTo] = useState('');
  const [method, setMethod] = useState('whatsapp');
  const smv = totalPartSMV(part?.processes, db);
  const msg = `*PART DETAILS*\nName: ${part?.name}\nGarment: ${part?.garmentType}\nFabric: ${part?.fabricType}\nDesign: ${part?.designAttr}\nSMV: ${smv.toFixed(4)} min\nProcesses: ${part?.processes?.length || 0}`;
  const send = () => {
    const enc = encodeURIComponent(msg);
    if (method === 'whatsapp') window.open(`https://wa.me/${to.replace(/\D/g, '')}?text=${enc}`, '_blank');
    else window.open(`mailto:${to}?subject=Part DNA: ${part?.name}&body=${encodeURIComponent(msg.replace(/\*/g, ''))}`, '_blank');
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1002 }}>
      <div style={{ background: 'white', borderRadius: '10px', padding: '24px', width: '400px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}><h3 style={{ margin: 0 }}>Share: {part?.name}</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button></div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          <button onClick={() => setMethod('whatsapp')} style={{ flex: 1, padding: '8px', background: method === 'whatsapp' ? '#00c875' : '#f0f1f4', color: method === 'whatsapp' ? 'white' : '#323338', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}><Phone size={14} /> WhatsApp</button>
          <button onClick={() => setMethod('email')} style={{ flex: 1, padding: '8px', background: method === 'email' ? '#0073ea' : '#f0f1f4', color: method === 'email' ? 'white' : '#323338', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}><Mail size={14} /> Email</button>
        </div>
        <input value={to} onChange={e => setTo(e.target.value)} placeholder={method === 'whatsapp' ? '+91 9876543210' : 'email@example.com'} style={{ width: '100%', padding: '8px', border: '1px solid #e6e9ef', borderRadius: '6px', marginBottom: '12px', boxSizing: 'border-box' }} />
        <div style={{ background: '#f5f6f8', padding: '10px', borderRadius: '6px', fontSize: '0.78rem', color: '#676879', marginBottom: '14px' }}>{msg.replace(/\*/g, '')}</div>
        <button onClick={send} style={{ width: '100%', padding: '10px', background: '#0073ea', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>SEND</button>
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  PATTERN TRACER  (no AI, no backend -- runs entirely in browser)
//  Upload a photo of a physical pattern piece, click points around
//  the outline, calibrate with a known measurement, and the system
//  calculates width / height / perimeter and exports a .npf file.
// -------------------------------------------------------------
const PatternTracer = ({ partName, partId, onComplete, onCancel, persistFn }) => {
  const [imgSrc, setImgSrc]       = useState(null);
  const [points, setPoints]       = useState([]);
  const [closed, setClosed]       = useState(false);
  const [scaleRefCm, setScaleRefCm] = useState('');
  const [pxPerCm, setPxPerCm]     = useState(1);
  const [scaleSet, setScaleSet]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [canvasSize, setCanvasSize] = useState({ w: 560, h: 460 });
  const canvasRef = useRef(null);

  // -- Load image ------------------------------------------------
  const handleUpload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const maxW = 560, maxH = 460;
        const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
        setCanvasSize({ w: Math.round(img.width * ratio), h: Math.round(img.height * ratio) });
        setImgSrc(ev.target.result);
        setPoints([]); setClosed(false); setScaleSet(false);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // -- Redraw canvas on any change -------------------------------
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvasSize.w, canvasSize.h);
    const drawOverlay = () => {
      if (points.length === 0) return;
      // Filled polygon when closed
      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);
      points.forEach(p => ctx.lineTo(p.x, p.y));
      if (closed) {
        ctx.closePath();
        ctx.fillStyle = 'rgba(37,99,235,0.12)';
        ctx.fill();
      }
      ctx.strokeStyle = '#0073ea'; ctx.lineWidth = 2;
      ctx.stroke();
      // Dot for each point
      points.forEach((p, i) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, i === 0 ? 7 : 4, 0, Math.PI * 2);
        ctx.fillStyle   = i === 0 ? '#e2445c' : '#0073ea';
        ctx.strokeStyle = 'white'; ctx.lineWidth = 1.5;
        ctx.fill(); ctx.stroke();
      });
      // Close-hint ring around first point when >=3 points and not yet closed
      if (points.length >= 3 && !closed) {
        ctx.beginPath();
        ctx.arc(points[0].x, points[0].y, 14, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(239,68,68,0.45)'; ctx.lineWidth = 1.5;
        ctx.setLineDash([3,3]); ctx.stroke(); ctx.setLineDash([]);
      }
    };
    if (imgSrc) {
      const img = new Image();
      img.onload = () => {
        ctx.globalAlpha = 0.45;
        ctx.drawImage(img, 0, 0, canvasSize.w, canvasSize.h);
        ctx.globalAlpha = 1;
        drawOverlay();
      };
      img.src = imgSrc;
    } else {
      drawOverlay();
    }
  }, [imgSrc, points, closed, canvasSize]);

  // -- Click handler ---------------------------------------------
  const handleClick = (e) => {
    if (closed) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasSize.w / rect.width;
    const scaleY = canvasSize.h / rect.height;
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top)  * scaleY;
    // Click near first point -> close shape
    if (points.length >= 3) {
      const dist = Math.hypot(x - points[0].x, y - points[0].y);
      if (dist < 16) { setClosed(true); return; }
    }
    setPoints(prev => [...prev, { x: +x.toFixed(1), y: +y.toFixed(1) }]);
  };

  // -- Scale calibration -----------------------------------------
  // Uses the bounding-box width of the traced outline against a
  // known real-world measurement the user types in.
  const calibrate = () => {
    const cm = parseFloat(scaleRefCm);
    if (!cm || points.length < 2) return;
    const xs = points.map(p => p.x);
    const pxW = Math.max(...xs) - Math.min(...xs);
    if (pxW < 5) return;
    setPxPerCm(pxW / cm);
    setScaleSet(true);
  };

  // -- Derived measurements ---------------------------------------
  const measurements = (() => {
    if (points.length < 3) return null;
    const xs = points.map(p => p.x), ys = points.map(p => p.y);
    const pxW = Math.max(...xs) - Math.min(...xs);
    const pxH = Math.max(...ys) - Math.min(...ys);
    let perim = 0;
    for (let i = 0; i < points.length; i++) {
      const a = points[i], b = points[(i + 1) % points.length];
      perim += Math.hypot(b.x - a.x, b.y - a.y);
    }
    const p = pxPerCm;
    return {
      width:     +(pxW   / p).toFixed(1),
      height:    +(pxH   / p).toFixed(1),
      perimeter: +(perim / p).toFixed(1),
    };
  })();

  // -- Build SVG path from points --------------------------------
  const svgPath = points.length >= 2
    ? points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') + ' Z'
    : '';

  // -- Create & save the NPF -------------------------------------
  const createNpf = async () => {
    const m = measurements || {};
    const np = {
      format:     'npf-v1',
      id:         `pat-${Date.now()}-traced`,
      partId:     partId || '',
      partName:   partName || 'Traced Pattern',
      template:   '',
      measurements: {
        ...(m.width     ? { width:     m.width }     : {}),
        ...(m.height    ? { height:    m.height }    : {}),
        ...(m.perimeter ? { perimeter: m.perimeter } : {}),
      },
      seamAllowances:  { top: 1, bottom: 1, left: 1, right: 1 },
      grainLine:       { angleDeg: 90 },
      notches: [], darts: [], measurementPoints: [],
      gradingRules: {}, specMapping: {}, brandRules: [],
      svgOutline:      svgPath,
      tracedPoints:    points.map(p => ({ x: +(p.x / pxPerCm).toFixed(2), y: +(p.y / pxPerCm).toFixed(2) })),
      sourceMethod:    'manual-trace',
      scalePixelsPerCm: +pxPerCm.toFixed(3),
      createdOn:       new Date().toISOString(),
      updatedOn:       new Date().toISOString(),
    };
    if (persistFn) {
      setSaving(true);
      const dbId = await persistFn(np).catch(() => null);
      setSaving(false);
      onComplete({ ...np, dbId });
    } else {
      onComplete(np);
    }
  };

  const canCreate = closed && points.length >= 3;

  return (
    <div style={{ padding:10, display:'flex', flexDirection:'column', gap:8 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        <button onClick={onCancel}
          style={{ padding:'4px 10px', background:'#f0f1f4', border:'none', borderRadius:4, cursor:'pointer', fontSize:11 }}>
          &larr; Back
        </button>
        <span style={{ fontWeight:'bold', fontSize:13, color:'#323338' }}>&#x270F; Trace Pattern Outline</span>
      </div>

      {/* Instructions */}
      <div style={{ fontSize:10, background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:6, padding:'6px 10px', color:'#0c4a6e', lineHeight:1.5 }}>
        <strong>How to use:</strong> Upload your pattern photo -&gt; click points around the outline -&gt;
        click the <span style={{ color:'#e2445c', fontWeight:'bold' }}>red dot</span> (or "Close") to finish the shape -&gt;
        enter a known width for calibration -&gt; click <strong>Create .npf</strong>.
      </div>

      {/* Upload area */}
      {!imgSrc && (
        <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
          height:180, border:'2px dashed #c5c7d4', borderRadius:8, cursor:'pointer', gap:8, color:'#9699a6', background:'#f5f6f8' }}>
          <span style={{ fontSize:36 }}>[cam]</span>
          <span style={{ fontSize:12 }}>Click to upload pattern photo or scan</span>
          <span style={{ fontSize:10, color:'#c5c7d4' }}>JPG \u00B7 PNG \u00B7 TIFF \u00B7 SVG</span>
          <input type="file" accept="image/*" hidden onChange={handleUpload} />
        </label>
      )}

      {/* Canvas */}
      {imgSrc && (
        <div>
          {/* Toolbar */}
          <div style={{ display:'flex', gap:5, marginBottom:6, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ fontSize:10, color:'#676879' }}>
              {points.length} pt{points.length !== 1 ? 's' : ''} \u00B7 {closed ? <span style={{ color:'#059669' }}>(/) Closed</span> : 'Open'}
            </span>
            <button onClick={() => { if (closed) { setClosed(false); } else { setPoints(p => p.slice(0,-1)); } }}
              style={{ padding:'3px 8px', background:'#fff5e6', border:'1px solid #fde68a', borderRadius:4, cursor:'pointer', fontSize:10 }}>
              \u21A9 Undo
            </button>
            {points.length >= 3 && !closed && (
              <button onClick={() => setClosed(true)}
                style={{ padding:'3px 8px', background:'#e3faf0', border:'1px solid #b3f5d6', borderRadius:4, cursor:'pointer', fontSize:10, color:'#166534', fontWeight:'bold' }}>
                Close Shape
              </button>
            )}
            <button onClick={() => { setPoints([]); setClosed(false); setScaleSet(false); }}
              style={{ padding:'3px 8px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, cursor:'pointer', fontSize:10, color:'#c93652' }}>
              Clear Points
            </button>
            <label style={{ padding:'3px 8px', background:'#f0f1f4', border:'1px solid #e6e9ef', borderRadius:4, cursor:'pointer', fontSize:10 }}>
              Change Image <input type="file" accept="image/*" hidden onChange={handleUpload} />
            </label>
          </div>

          {/* Drawing canvas */}
          <div style={{ border:'2px solid', borderColor: closed ? '#059669' : '#0073ea', borderRadius:6, overflow:'hidden', display:'inline-block', maxWidth:'100%' }}>
            <canvas ref={canvasRef}
              width={canvasSize.w} height={canvasSize.h}
              onClick={handleClick}
              style={{ display:'block', cursor: closed ? 'default' : 'crosshair', maxWidth:'100%' }}
            />
          </div>

          {/* Scale calibration -- shown after closing shape */}
          {closed && (
            <div style={{ marginTop:8, padding:10, background:'#fffbeb', border:'1px solid #fde68a', borderRadius:6 }}>
              <div style={{ fontSize:10, fontWeight:'bold', color:'#92400e', marginBottom:5 }}>
                [ruler] Calibrate -- enter the actual width of the pattern piece:
              </div>
              <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
                <input type="number" placeholder="e.g. 54" value={scaleRefCm}
                  onChange={e => { setScaleRefCm(e.target.value); setScaleSet(false); }}
                  style={{ width:70, padding:'4px 6px', border:'1px solid #fde68a', borderRadius:4, fontSize:11 }} />
                <span style={{ fontSize:10, color:'#92400e' }}>cm</span>
                <button onClick={calibrate}
                  style={{ padding:'4px 12px', background:'#fdab3d', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:10, fontWeight:'bold' }}>
                  (/) Set Scale
                </button>
                {scaleSet && (
                  <span style={{ fontSize:10, color:'#166534', background:'#e3faf0', padding:'2px 8px', borderRadius:4 }}>
                    (/) Scale: {pxPerCm.toFixed(1)} px/cm
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Calculated measurements */}
          {measurements && (
            <div style={{ marginTop:8, display:'flex', gap:12, flexWrap:'wrap', fontSize:11,
              background:'#f0fdf4', border:'1px solid #b3f5d6', borderRadius:6, padding:'8px 12px' }}>
              <span>[spec] Width: <strong>{measurements.width} cm</strong></span>
              <span>[ruler] Height: <strong>{measurements.height} cm</strong></span>
              <span>[loop] Perimeter: <strong>{measurements.perimeter} cm</strong></span>
              {!scaleSet && <span style={{ color:'#fdab3d', fontSize:10 }}>\u26A0 Set scale above for accurate values</span>}
            </div>
          )}

          {/* Create NPF */}
          {canCreate && (
            <button onClick={createNpf} disabled={saving}
              style={{ marginTop:10, padding:'9px 22px', background: saving ? '#9699a6' : '#a25ddc',
                color:'white', border:'none', borderRadius:6, cursor: saving ? 'not-allowed' : 'pointer',
                fontSize:12, fontWeight:'bold', display:'flex', alignItems:'center', gap:6 }}>
              {saving ? '\u27F3 Saving...' : '(/) Create Pattern File (.npf) from Trace'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------
//  PATTERN SECTION  (API-backed)
//  Lives inside the "Patterns & Specs" tab of the sketch panel.
//  * Stores patterns in part_patterns DB table (separate from dna_data)
//  * Each pattern carries: SA, grain line, SPECS (measurements),
//    measurement points, notches
//  * .npf upload auto-reads all stored measurements -> displayed immediately
//  * AI Extract: send a photo of a physical pattern -> Claude reads shape
//    and measurements and pre-fills a new pattern record
// -------------------------------------------------------------
const API_PAT = 'https://nirvikalp.integrashion.com/api';

const PatternSection = ({ dna }) => {
  const [patterns, setPatterns]       = useState([]);
  const [editIdx, setEditIdx]         = useState(null);
  const [loading, setLoading]         = useState(false);
  const [showTracer, setShowTracer]   = useState(false);  // click-to-trace tool
  const [aiLoading, setAiLoading]     = useState(false);
  const [aiStatus, setAiStatus]       = useState('');
  const [newSpecKey, setNewSpecKey]   = useState('');
  const [newSpecVal, setNewSpecVal]   = useState('');
  const [newMpLabel, setNewMpLabel]   = useState('');
  const [newMpDist, setNewMpDist]     = useState('');
  const npfRef  = useRef(null);
  const aiRef   = useRef(null);

  // -- Load patterns from DB when part changes ------------------------------
  useEffect(() => {
    const partId = dna.id;
    const isReal = partId && Number(partId) > 0 && Number(partId) < 2000000000;
    if (!isReal) { setPatterns([]); return; }
    setLoading(true);
    fetch(`${API_PAT}/get_patterns.php?part_id=${partId}`)
      .then(r => r.json())
      .then(d => { if (d.success) setPatterns(d.patterns || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dna.id]);

  // -- Save a pattern to DB; returns dbId ----------------------------------
  const persist = async (pat) => {
    const res = await fetch(`${API_PAT}/save_universal.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category: 'pattern',
        data: { ...pat, partId: dna.id, partName: dna.name }
      })
    });
    const r = await res.json().catch(() => ({}));
    return r.dbId || null;
  };

  const mutate = async (idx, updated) => {
    await persist(updated);
    setPatterns(prev => prev.map((p, i) => i === idx ? updated : p));
  };

  // -- Add a blank pattern --------------------------------------------------
  const addBlank = async () => {
    const isReal = dna.id && Number(dna.id) > 0 && Number(dna.id) < 2000000000;
    if (!isReal) { alert('Save the part first, then add patterns.'); return; }
    const np = {
      format: 'npf-v1',
      id: `pat-${Date.now()}-${Math.random().toString(36).slice(2,6)}`,
      partId: dna.id, partName: dna.name || 'Part',
      template: dna.specs?.template || '',
      measurements: { ...(dna.specs?.measurements || {}) },  // pre-fill from part specs
      seamAllowances: { top: 1, bottom: 1, left: 1, right: 1 },
      grainLine: { angleDeg: 90 },
      notches: [], darts: [], measurementPoints: [],
      gradingRules: {}, specMapping: {}, brandRules: [], svgOutline: '',
      createdOn: new Date().toISOString(), updatedOn: new Date().toISOString(),
    };
    const dbId = await persist(np);
    setPatterns(prev => [...prev, { ...np, dbId }]);
    setEditIdx(patterns.length);
  };

  // -- Import .npf -- auto-reads all stored measurements --------------------
  // Answers Q4: "Is it possible to read measurements if pattern file is uploaded?"
  // -> YES. .npf is JSON, measurements field is decoded and displayed in the editor.
  const importNpf = async (e) => {
    const isReal = dna.id && Number(dna.id) > 0 && Number(dna.id) < 2000000000;
    if (!isReal) { alert('Save the part first before importing patterns.'); e.target.value=''; return; }
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const p = JSON.parse(ev.target.result);
        if (!p.format?.startsWith('npf')) { alert('Not a valid .npf pattern file'); return; }
        const toSave = { ...p, id:`pat-${Date.now()}`, partId: dna.id, partName: dna.name };
        const dbId = await persist(toSave);
        const newList = [...patterns, { ...toSave, dbId }];
        setPatterns(newList);
        setEditIdx(newList.length - 1);  // open editor so measurements are visible immediately
        setAiStatus(`(/) Pattern imported -- ${Object.keys(p.measurements||{}).length} measurement(s) loaded`);
        setTimeout(() => setAiStatus(''), 4000);
      } catch (err) { alert('Import failed: ' + err.message); }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  // -- AI image -> pattern file ----------------------------------------------
  // Answers Q5: "Is it possible to convert pattern image into pattern file
  //              and auto-modify specs and shape?"
  // -> YES using Claude vision. Send the photo; get back measurements + shape.
  const aiExtract = async (e) => {
    const isReal = dna.id && Number(dna.id) > 0 && Number(dna.id) < 2000000000;
    if (!isReal) { alert('Save the part first before using AI extraction.'); e.target.value=''; return; }
    const file = e.target.files?.[0]; if (!file) return;
    setAiLoading(true);
    setAiStatus('[AI] Sending image to AI -- reading shape and measurements...');
    try {
      const reader = new FileReader();
      const b64 = await new Promise((res,rej) => {
        reader.onload = ev => res(ev.target.result.split(',')[1]);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const mediaType = file.type || 'image/jpeg';

      const response = await fetch(`${API_PAT}/ai_pattern_extract.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: b64, mediaType, partName: dna.name, template: dna.specs?.template || '' })
      });
      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'Extraction failed');

      const extracted = result.pattern;
      const np = {
        format: 'npf-v1',
        id: `pat-${Date.now()}-ai`,
        partId: dna.id,
        partName: extracted.partName || dna.name,
        template: dna.specs?.template || '',
        measurements: extracted.measurements || {},
        seamAllowances: extracted.seamAllowances || { top: 1, bottom: 1, left: 1, right: 1 },
        grainLine: extracted.grainLine || { angleDeg: 90 },
        notches: extracted.notches || [],
        darts: [],
        measurementPoints: [],
        shape: extracted.shape || '',
        aiExtracted: true,
        gradingRules: {}, specMapping: {}, brandRules: [], svgOutline: '',
        createdOn: new Date().toISOString(), updatedOn: new Date().toISOString(),
      };
      const dbId = await persist(np);
      const newList = [...patterns, { ...np, dbId }];
      setPatterns(newList);
      setEditIdx(newList.length - 1);
      const mCount = Object.keys(np.measurements).length;
      setAiStatus(`(/) AI extracted ${mCount} measurement(s) from image -- review and adjust below`);
      setTimeout(() => setAiStatus(''), 6000);
    } catch (err) {
      setAiStatus('\u26A0 AI extraction failed: ' + err.message);
      setTimeout(() => setAiStatus(''), 5000);
    } finally {
      setAiLoading(false);
      e.target.value = '';
    }
  };

  // -- Export .npf ----------------------------------------------------------
  const exportNpf = (pat) => {
    const blob = new Blob([JSON.stringify(pat, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(pat.partName || 'pattern').replace(/\s+/g,'_')}_${pat.id}.npf`;
    a.click();
  };

  // -- Field updaters -------------------------------------------------------
  const updateSA = async (idx, side, val) => {
    const up = { ...patterns[idx], seamAllowances: { ...patterns[idx].seamAllowances, [side]: parseFloat(val)||0 }, updatedOn: new Date().toISOString() };
    await mutate(idx, up);
  };
  const updateGrain = async (idx, val) => {
    const up = { ...patterns[idx], grainLine: { ...patterns[idx].grainLine, angleDeg: +val }, updatedOn: new Date().toISOString() };
    await mutate(idx, up);
  };
  const updateMeasurement = async (idx, key, val) => {
    const up = { ...patterns[idx], measurements: { ...patterns[idx].measurements, [key]: parseFloat(val)||0 }, updatedOn: new Date().toISOString() };
    await mutate(idx, up);
  };
  const addMeasurement = async (idx) => {
    if (!newSpecKey.trim()) return;
    const up = { ...patterns[idx], measurements: { ...(patterns[idx].measurements||{}), [newSpecKey.trim()]: parseFloat(newSpecVal)||0 }, updatedOn: new Date().toISOString() };
    await mutate(idx, up);
    setNewSpecKey(''); setNewSpecVal('');
  };
  const removeMeasurement = async (idx, key) => {
    const m = { ...(patterns[idx].measurements||{}) };
    delete m[key];
    const up = { ...patterns[idx], measurements: m, updatedOn: new Date().toISOString() };
    await mutate(idx, up);
  };
  const addMeasurementPoint = async (idx) => {
    if (!newMpLabel.trim()) return;
    const mp = { id: `mp-${Date.now()}`, label: newMpLabel.trim(), dist: parseFloat(newMpDist)||0 };
    const up = { ...patterns[idx], measurementPoints: [...(patterns[idx].measurementPoints||[]), mp], updatedOn: new Date().toISOString() };
    await mutate(idx, up);
    setNewMpLabel(''); setNewMpDist('');
  };
  const removeMeasurementPoint = async (idx, mpId) => {
    const up = { ...patterns[idx], measurementPoints: (patterns[idx].measurementPoints||[]).filter(m=>m.id!==mpId), updatedOn: new Date().toISOString() };
    await mutate(idx, up);
  };
  const removePattern = async (idx) => {
    if (!window.confirm('Remove this pattern?')) return;
    const pat = patterns[idx];
    if (pat.dbId) {
      await fetch(`${API_PAT}/save_universal.php`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ category:'pattern', data:{ dbId: pat.dbId, _deleted:true } })
      });
    }
    setPatterns(prev => prev.filter((_,i)=>i!==idx));
    if (editIdx === idx) setEditIdx(null);
  };

  const isReal = dna.id && Number(dna.id) > 0 && Number(dna.id) < 2000000000;

  // -- Tracer overlay -- replaces normal PatternSection UI -----------------
  if (showTracer) {
    return (
      <PatternTracer
        partName={dna.name}
        partId={dna.id}
        persistFn={persist}
        onComplete={(np) => {
          setPatterns(prev => [...prev, np]);
          setShowTracer(false);
          setEditIdx(patterns.length);  // open editor for the new pattern
        }}
        onCancel={() => setShowTracer(false)}
      />
    );
  }

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>

      {/* -- Header ----------------------------------------------------------- */}
      <div style={{ background:'#a25ddc', padding:'8px 12px', display:'flex', justifyContent:'space-between', alignItems:'center', gap:6, flexWrap:'wrap', flexShrink:0 }}>
        <span style={{ fontWeight:'bold', fontSize:'0.82rem', color:'white' }}>
          [patt] Patterns & Specs {loading ? '-- loading...' : `-- ${patterns.length} stored`}
        </span>
        <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
          <button onClick={addBlank}
            style={{ padding:'3px 10px', background:'white', color:'#a25ddc', border:'none', borderRadius:4, cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold' }}>
            + New
          </button>
          <label style={{ padding:'3px 10px', background:'#f3eafb', color:'#a25ddc', borderRadius:4, cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:3 }}>
            \u2191 .npf
            <input ref={npfRef} type="file" accept=".npf,.json" hidden onChange={importNpf} />
          </label>
          <button onClick={() => setShowTracer(true)}
            title="Upload a pattern photo and click-trace the outline -- no AI needed"
            style={{ padding:'3px 10px', background:'#ecfdf5', color:'#059669', borderRadius:4, cursor:'pointer', fontSize:'0.72rem', fontWeight:'bold', border:'1px solid #7becc0' }}>
            &#x270F; Trace Pattern
          </button>
          <label style={{ padding:'3px 10px', background: aiLoading ? '#f5d0fe' : '#fafafa', color: aiLoading ? '#a25ddc' : '#9699a6', borderRadius:4, cursor:'pointer', fontSize:'0.70rem', display:'flex', alignItems:'center', gap:3, border:'1px solid #e6e9ef' }}
            title="Requires Anthropic API key configured on server">
            {aiLoading ? '\u27F3 AI...' : '[AI] AI (needs key)'}
            <input ref={aiRef} type="file" accept="image/*" hidden onChange={aiExtract} disabled={aiLoading} />
          </label>
        </div>
      </div>

      {/* -- Status message --------------------------------------------------- */}
      {aiStatus && (
        <div style={{ background: aiStatus.startsWith('\u26A0') ? '#fef2f2' : '#f0fdf4', borderBottom:'1px solid #e6e9ef', padding:'5px 12px', fontSize:'0.75rem', color: aiStatus.startsWith('\u26A0') ? '#c93652' : '#166534' }}>
          {aiStatus}
        </div>
      )}

      {/* -- Body ------------------------------------------------------------- */}
      <div style={{ flex:1, overflowY:'auto', padding:10, background:'white' }}>

        {/* Info strip */}
        <div style={{ fontSize:'0.68rem', color:'#8b3fd4', background:'#f5f3ff', border:'1px solid #ddd6fe', borderRadius:5, padding:'4px 8px', marginBottom:8 }}>
          <strong>.npf upload</strong> auto-reads all stored measurements.
          <strong> AI Extract</strong> converts a pattern photo into a pre-filled pattern record. 
          Patterns stored here are picked up automatically by <strong>Marker Maker</strong>.
        </div>

        {/* Must save first warning */}
        {!isReal && (
          <div style={{ padding:'12px', color:'#92400e', fontSize:'0.78rem', border:'1px solid #fde68a', borderRadius:6, background:'#fffbeb', textAlign:'center' }}>
            \u26A0 Save this part first -- then patterns can be stored against it.
          </div>
        )}

        {/* Empty state */}
        {isReal && !loading && patterns.length === 0 && (
          <div style={{ textAlign:'center', padding:'20px', color:'#9699a6', fontSize:'0.78rem', border:'1px dashed #ddd6fe', borderRadius:6 }}>
            No patterns yet. Click <strong>+ New</strong> or <strong>\u2191 .npf</strong> or use <strong>[AI] AI Extract</strong> to create one from a photo.
          </div>
        )}

        {/* Pattern cards */}
        {patterns.map((p, i) => (
          <div key={p.dbId||p.id||i} style={{ border: editIdx===i ? '2px solid #a25ddc' : '1px solid #e6e9ef', borderRadius:7, marginBottom:8, overflow:'hidden' }}>

            {/* Card header */}
            <div style={{ background: editIdx===i ? '#f5f3ff' : '#fafafa', padding:'7px 10px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <span style={{ fontWeight:'bold', fontSize:'0.82rem', color:'#323338' }}>{p.partName||'Unnamed'}</span>
                {p.aiExtracted && <span style={{ marginLeft:6, fontSize:'0.62rem', background:'#f3eafb', color:'#a25ddc', padding:'1px 5px', borderRadius:10 }}>AI</span>}
                <span style={{ marginLeft:8, fontSize:'0.68rem', color:'#676879' }}>
                  {Object.keys(p.measurements||{}).length} spec(s) \u00B7 {(p.measurementPoints||[]).length} point(s)
                </span>
              </div>
              <div style={{ display:'flex', gap:4 }}>
                <button onClick={() => setEditIdx(editIdx===i ? null : i)}
                  style={{ padding:'3px 8px', background: editIdx===i?'#a25ddc':'#f0f1f4', color: editIdx===i?'white':'#a25ddc', border:'1px solid #ddd6fe', borderRadius:4, cursor:'pointer', fontSize:'0.68rem' }}>
                  {editIdx===i ? '\u25B2' : '\u270E Edit'}
                </button>
                <button onClick={() => exportNpf(p)}
                  style={{ padding:'3px 8px', background:'#f0fdf4', border:'1px solid #b3f5d6', borderRadius:4, cursor:'pointer', fontSize:'0.68rem', color:'#15803d' }}>
                  \u2193 .npf
                </button>
                <button onClick={() => removePattern(i)}
                  style={{ padding:'3px 6px', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:4, cursor:'pointer', fontSize:'0.68rem', color:'#c93652' }}>
                  (x)
                </button>
              </div>
            </div>

            {/* Expanded editor */}
            {editIdx === i && (
              <div style={{ padding:'10px 12px', borderTop:'1px solid #e9d5ff', display:'flex', flexDirection:'column', gap:12 }}>

                {/* -- Seam Allowances + Grain -- */}
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <div>
                    <div style={{ fontSize:'0.66rem', fontWeight:'bold', color:'#676879', textTransform:'uppercase', marginBottom:5 }}>Seam Allowances (cm)</div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
                      {['top','right','bottom','left'].map(side => (
                        <div key={side}>
                          <label style={{ fontSize:'0.62rem', color:'#9699a6', textTransform:'capitalize', display:'block' }}>{side}</label>
                          <input type="number" step="0.1" min="0" max="5"
                            defaultValue={p.seamAllowances?.[side]??1}
                            onBlur={e => updateSA(i, side, e.target.value)}
                            style={{ width:'100%', padding:'3px 4px', border:'1px solid #ddd', borderRadius:3, fontSize:'0.75rem', boxSizing:'border-box' }} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize:'0.66rem', fontWeight:'bold', color:'#676879', textTransform:'uppercase', marginBottom:5 }}>Grain Line Angle (deg)</div>
                    <input type="number" min={0} max={360}
                      defaultValue={p.grainLine?.angleDeg??90}
                      onBlur={e => updateGrain(i, e.target.value)}
                      style={{ width:'100%', padding:'4px 5px', border:'1px solid #ddd', borderRadius:3, fontSize:'0.78rem', boxSizing:'border-box', marginBottom:4 }} />
                    <div style={{ fontSize:'0.62rem', color:'#8b3fd4', background:'#f5f3ff', padding:'3px 6px', borderRadius:3 }}>
                      90deg = warp (vertical/correct for all patterns)
                    </div>
                    {p.shape && (
                      <div style={{ marginTop:5, fontSize:'0.65rem', color:'#676879', background:'#f5f6f8', padding:'4px 6px', borderRadius:3, fontStyle:'italic' }}>
                        Shape: {p.shape}
                      </div>
                    )}
                  </div>
                </div>

                {/* -- Specs / Measurements -- */}
                <div>
                  <div style={{ fontSize:'0.66rem', fontWeight:'bold', color:'#676879', textTransform:'uppercase', marginBottom:5 }}>
                    Specs / Measurements
                    <span style={{ fontWeight:'normal', marginLeft:6, color:'#9699a6' }}>-- pre-fed to Marker Maker when pattern is picked up</span>
                  </div>
                  {Object.keys(p.measurements||{}).length === 0 ? (
                    <div style={{ fontSize:'0.70rem', color:'#9699a6', marginBottom:6 }}>No measurements yet.</div>
                  ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px,1fr))', gap:5, marginBottom:8 }}>
                      {Object.entries(p.measurements||{}).map(([k,v]) => (
                        <div key={k} style={{ background:'#f5f6f8', border:'1px solid #e6e9ef', borderRadius:5, padding:'4px 7px', display:'flex', alignItems:'center', gap:4 }}>
                          <span style={{ fontSize:'0.68rem', color:'#676879', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{k}</span>
                          <input type="number" step="0.1"
                            defaultValue={v}
                            onBlur={e => updateMeasurement(i, k, e.target.value)}
                            style={{ width:46, padding:'2px 3px', border:'1px solid #ddd', borderRadius:3, fontSize:'0.72rem', textAlign:'right' }} />
                          <span style={{ fontSize:'0.60rem', color:'#9699a6' }}>cm</span>
                          <button onClick={() => removeMeasurement(i, k)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#c93652', fontSize:'0.68rem', padding:0, lineHeight:1 }}>(x)</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Add new measurement */}
                  <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                    <input placeholder="Name (e.g. Chest)" value={newSpecKey} onChange={e=>setNewSpecKey(e.target.value)}
                      style={{ flex:1, padding:'4px 6px', border:'1px solid #ddd', borderRadius:4, fontSize:'0.72rem' }} />
                    <input type="number" placeholder="cm" value={newSpecVal} onChange={e=>setNewSpecVal(e.target.value)}
                      style={{ width:60, padding:'4px 5px', border:'1px solid #ddd', borderRadius:4, fontSize:'0.72rem' }} />
                    <button onClick={() => addMeasurement(i)}
                      style={{ padding:'4px 10px', background:'#a25ddc', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:'0.72rem' }}>+ Add</button>
                  </div>
                </div>

                {/* -- Measurement Points -- */}
                <div>
                  <div style={{ fontSize:'0.66rem', fontWeight:'bold', color:'#676879', textTransform:'uppercase', marginBottom:5 }}>
                    Measurement Points
                    <span style={{ fontWeight:'normal', marginLeft:6, color:'#9699a6' }}>-- labelled reference distances on the pattern</span>
                  </div>
                  {(p.measurementPoints||[]).length === 0 ? (
                    <div style={{ fontSize:'0.70rem', color:'#9699a6', marginBottom:6 }}>No points yet.</div>
                  ) : (
                    <div style={{ marginBottom:7 }}>
                      {(p.measurementPoints||[]).map(mp => (
                        <div key={mp.id} style={{ display:'flex', alignItems:'center', gap:6, padding:'4px 0', borderBottom:'1px solid #f0f1f4', fontSize:'0.72rem' }}>
                          <span style={{ flex:1, color:'#323338', fontWeight:'bold' }}>{mp.label}</span>
                          <span style={{ color:'#059669', fontWeight:'bold' }}>{mp.dist} cm</span>
                          <button onClick={() => removeMeasurementPoint(i, mp.id)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'#c93652', fontSize:'0.68rem' }}>(x)</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                    <input placeholder="Label (e.g. Neckline)" value={newMpLabel} onChange={e=>setNewMpLabel(e.target.value)}
                      style={{ flex:1, padding:'4px 6px', border:'1px solid #ddd', borderRadius:4, fontSize:'0.72rem' }} />
                    <input type="number" placeholder="dist cm" value={newMpDist} onChange={e=>setNewMpDist(e.target.value)}
                      style={{ width:70, padding:'4px 5px', border:'1px solid #ddd', borderRadius:4, fontSize:'0.72rem' }} />
                    <button onClick={() => addMeasurementPoint(i)}
                      style={{ padding:'4px 10px', background:'#0ea5e9', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:'0.72rem' }}>+ Add</button>
                  </div>
                </div>

              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// -------------------------------------------------------------
//  MAIN PARTDNA COMPONENT
// -------------------------------------------------------------
export default function PartDNA({ db, setDb, saveToDB }) {
  const [activeView, setActiveView] = useState('gallery');
  const [editId, setEditId] = useState(null);
  const [dna, setDna] = useState({ name: '', garmentType: '', fabricType: '', category: '', designAttr: '', image: '', dnaL: 20, dnaW: 15, processes: [], patterns: [], createdBy: '', createdOn: '', approvedBy: '', isApproved: false, specs: null });
  const dnaRef = useRef(dna); // always holds latest dna - avoids stale closure in finalize
  const [proc, setProc] = useState({ name: '', attachment: '', video: '', ops: [], seamDiagramId: '', seamDiagramNote: '' });

  // Keep dnaRef in sync with dna state so finalize always reads the latest image
  useEffect(() => { dnaRef.current = dna; }, [dna]);
  const [studioStep, setStudioStep] = useState(1); // 1=Identity 2=Sketch 3=Processes 4=Patterns
  const [sketchMode, setSketchMode] = useState('upload'); // 'upload' | 'draw' | 'spec' | 'patterns'
  const [sketchFullscreen, setSketchFullscreen] = useState(false);
  const [bgThreshold, setBgThreshold] = useState(240);
  const [bgRemoving, setBgRemoving]   = useState(false);
  const [targetEff, setTargetEff]     = useState(75);  // planned efficiency %
  const [videoModal, setVideoModal] = useState(null);
  const [smartClone, setSmartClone] = useState(null); // triggers smart clone search
  const [printProcess, setPrintProcess] = useState(null);
  const [viewPart, setViewPart] = useState(null);
  const [shareModal, setShareModal] = useState(null);
  const [editingProcessIdx, setEditingProcessIdx] = useState(null);
  const [imageSaved, setImageSaved] = useState(false);

  // -- Fabric data - read from db prop (parent loads via get_all_data.php)
  // If not yet available, fetch directly using the same endpoint the parent uses
  const [localFabrics, setLocalFabrics]   = useState([]);
  const [fabricsLoading, setFabricsLoading] = useState(false);

  useEffect(() => {
    // If parent db already has fabrics, use them immediately
    const fromDb = db.fabricDifficulties || [];
    if (fromDb.length > 0) {
      setLocalFabrics(fromDb);
      return;
    }
    // Parent db not loaded yet - fetch directly from get_all_data.php
    setFabricsLoading(true);
    fetch('https://nirvikalp.integrashion.com/api/get_all_data.php', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(r => r.json())
      .then(data => {
        // Try every likely key name the PHP might return
        // Normalise fabrics from every possible DB key and field name
        const rawFabrics =
          data.fabricDifficulties ||
          data.fabric_difficulties ||
          data.fabricdifficulties ||
          data.fabrics ||           // common_masters type=FABRIC
          [];
        const fabrics = rawFabrics.map((f, idx) => ({
          id:     f.id     || f._id    || idx + 1,
          fabric: f.fabric || f.name   || f.fabricName || f.label || String(f),
          factor: parseFloat(f.factor  || f.difficultyFactor || f.difficulty ||
                             f.extra   || f.value || 0),
        })).filter(f => f.fabric && f.fabric.length > 0);
        if (fabrics.length > 0) {
          setLocalFabrics(fabrics);
          setDb(prev => ({ ...prev, fabricDifficulties: fabrics }));
        }
      })
      .catch(() => {}) // silent - dropdown shows "No fabrics loaded" hint
      .finally(() => setFabricsLoading(false));
  }, []); // eslint-disable-line

  // Keep in sync if parent db updates after mount
  useEffect(() => {
    if ((db.fabricDifficulties || []).length > 0) {
      setLocalFabrics(db.fabricDifficulties);
    }
  }, [db.fabricDifficulties]);

  // Merged + deduplicated fabric list
  const fabricOptions = useMemo(() => {
    const combined = [...localFabrics, ...(db.fabricDifficulties || []), ...(db.fabrics || [])];
    const seen = new Set();
    return combined
      .map((f, idx) => ({
        id:     f.id     || f._id    || idx + 1,
        fabric: f.fabric || f.name   || f.fabricName || '',
        factor: parseFloat(f.factor  || f.difficultyFactor || f.difficulty || 0),
      }))
      .filter(f => {
        if (!f.fabric || seen.has(f.fabric.toLowerCase())) return false;
        seen.add(f.fabric.toLowerCase());
        return true;
      });
  }, [localFabrics, db.fabricDifficulties, db.fabrics]);

  const totalSMV = totalPartSMV(dna.processes, db);

  // -- Image save callback from SketchCanvas ------------------
  // FIX #5: Image is explicitly committed to dna.image state AND user gets confirmation
  const handleSketchSave = useCallback((dataUrl, tracedPoints) => {
    // tracedPoints may be passed from SpecDrivenShape when node overrides exist
    setDna(prev => ({
      ...prev,
      image: dataUrl,
      ...(tracedPoints ? { tracedPoints } : {}),
    }));
    setImageSaved(true);
    setTimeout(() => setImageSaved(false), 3000);
  }, []);

  // -- File upload --------------------------------------------
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setDna(prev => ({ ...prev, image: b64 }));
    setImageSaved(true);
    setTimeout(() => setImageSaved(false), 3000);
    // Trigger smart clone search when image is uploaded for a NEW part
    const partsWithImages = (db.masterParts || db.parts || []).filter(p => p.image && p.id !== editId);
    if (partsWithImages.length > 0 && !editId) {
      setSmartClone({ newImage: b64, newPartName: dna.name || '' });
    }
  };

  const handleRemoveBackground = async () => {
    if (!dna.image) return;
    setBgRemoving(true);
    const cleaned = await removeWhiteBackground(dna.image, bgThreshold);
    // removeWhiteBackground returns a canvas toDataURL - ensure it is PNG
    // so the transparent pixels are not lost when saved
    const asPng = cleaned.startsWith('data:image/png') ? cleaned : cleaned;
    setDna(prev => ({ ...prev, image: asPng }));
    setBgRemoving(false);
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const b64 = await fileToBase64(file);
    setProc(prev => ({ ...prev, video: b64 }));
  };

  // -- Process mgmt -------------------------------------------
  const addOrUpdateProcess = (skipChecks = false) => {
    if (!proc.name) return alert('Process name required');

    // -- Auto-check: warn if no trim or dispose codes in ops --
    if (!skipChecks && proc.ops.length > 0) {
      const hasTrim    = proc.ops.some(o => {
        const mc = db.machines?.find(m => m.id == o.mcId);
        const mn = db.manualCodes?.find(m => m.id == o.refId);
        const label = (mc?.code || mn?.code || '').toUpperCase();
        return label.includes('TRIM') || label.includes('CUT') || label.includes('CLIP') || label.includes('SNIP');
      });
      const hasDispose = proc.ops.some(o => {
        const mc = db.machines?.find(m => m.id == o.mcId);
        const mn = db.manualCodes?.find(m => m.id == o.refId);
        const label = (mc?.code || mn?.code || '').toUpperCase();
        return label.includes('DISP') || label.includes('FOLD') || label.includes('PRESS') || label.includes('TURN');
      });
      const warnings = [];
      if (!hasTrim)    warnings.push('No TRIM / CUT code found');
      if (!hasDispose) warnings.push('No DISPOSE / FOLD / PRESS code found');
      if (warnings.length > 0) {
        const proceed = window.confirm(
          'QC Warning - process may be incomplete:\n\n' +
          warnings.map(w => '* ' + w).join('\n') +
          '\n\nClick OK to add anyway, or Cancel to go back and add missing codes.'
        );
        if (!proceed) return;
      }
    }

    // -- Generate unique process code --
    const procCode = proc.procCode || (() => {
      const existingCodes = dna.processes.map(p => p.procCode || '').filter(Boolean);
      let n = dna.processes.length + 1;
      let code;
      do { code = 'P' + String(n).padStart(3, '0'); n++; } while (existingCodes.includes(code));
      return code;
    })();

    const ts = new Date().toLocaleDateString();
    if (editingProcessIdx !== null) {
      const updated = [...dna.processes];
      updated[editingProcessIdx] = { ...proc, procCode, editedBy: dna.createdBy || 'User', editedOn: ts };
      setDna(prev => ({ ...prev, processes: updated }));
      setEditingProcessIdx(null);
    } else {
      setDna(prev => ({ ...prev, processes: [...prev.processes, { ...proc, procCode, createdBy: dna.createdBy || 'User', createdOn: ts }] }));
    }
    setProc({ name: '', attachment: '', video: '', purpose: '', ops: [], seamDiagramId: '', seamDiagramNote: '' });
  };

  const editProcess = (idx) => { setProc({ ...dna.processes[idx] }); setEditingProcessIdx(idx); };
  const deleteProcess = (idx) => { if (window.confirm('Remove process?')) setDna(prev => ({ ...prev, processes: prev.processes.filter((_, i) => i !== idx) })); };

  // -- Save part ----------------------------------------------
  // Call save_universal.php directly with all 3 required params (category, type, data)
  // so the image is guaranteed to be included in the payload.
  const handleSmartClone = (clonedData) => {
    // Apply cloned data to current dna state
    setDna(prev => ({
      ...prev,
      garmentType:  clonedData.garmentType  || prev.garmentType,
      fabricType:   clonedData.fabricType   || prev.fabricType,
      designAttr:   clonedData.designAttr   || prev.designAttr,
      processes:    clonedData.processes    || [],
      clonedFrom:   clonedData.clonedFrom,
      clonedFromName: clonedData.clonedFromName,
    }));
    setSmartClone(null);
  };

  const finalize = async () => {
    // Read from dnaRef.current so we always get the latest state
    // (avoids stale closure bug where old dna.image is used after removeBackground)
    const currentDna = dnaRef.current;

    if (!currentDna.name) return alert('Part Name is required');
    if (!currentDna.createdBy || !currentDna.createdBy.trim())
      return alert('Created By is required -- please enter your name before saving');

    const imageData = currentDna.image || '';

    // Compress image before sending so it fits within PHP post_max_size.
    // IMPORTANT: if the image has transparency (PNG after bg removal), we must
    // NOT fill a white background and must output as PNG not JPEG.
    // JPEG discards the alpha channel entirely, making the bg white again.
    let compressedImage = imageData;
    if (imageData && imageData.startsWith('data:')) {
      try {
        compressedImage = await new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            const c = document.createElement('canvas');
            const MAX = 600;
            let { width: w, height: h } = img;
            if (w > MAX || h > MAX) {
              if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
              else { w = Math.round(w * MAX / h); h = MAX; }
            }
            c.width = w; c.height = h;
            const ctx = c.getContext('2d');

            // Check if the source image is a transparent PNG (background was removed)
            // by looking at whether the data URI is PNG format
            const isPng = imageData.startsWith('data:image/png');

            if (!isPng) {
              // Opaque image (JPEG upload) - safe to fill white and compress as JPEG
              ctx.fillStyle = '#fff';
              ctx.fillRect(0, 0, w, h);
              ctx.drawImage(img, 0, 0, w, h);
              resolve(c.toDataURL('image/jpeg', 0.75));
            } else {
              // Transparent PNG (background removed) - no white fill, keep as PNG
              // Use lower quality via smaller size only; PNG has no quality param
              ctx.clearRect(0, 0, w, h); // ensure transparent background
              ctx.drawImage(img, 0, 0, w, h);
              resolve(c.toDataURL('image/png'));
            }
          };
          img.onerror = () => resolve(imageData);
          img.src = imageData;
        });
      } catch (_) { compressedImage = imageData; }
    }

    const partData = {
      ...currentDna,
      image: compressedImage,
      createdOn: currentDna.createdOn || new Date().toLocaleDateString()
    };

    try {
      // Direct fetch - we control the exact payload sent to save_universal.php.
      // This removes all ambiguity about what the parent saveToDB prop does.
      const dataToSave = editId ? { ...partData, id: editId } : { ...partData, id: undefined };

      const res = await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'part', type: 'PART', data: dataToSave })
      });

      const text = await res.text(); // read as text first so we never crash on bad JSON
      let result = {};
      try { result = JSON.parse(text); } catch (_) { result = { raw: text }; }

      console.log('PartDNA save_universal response:', result);

      // A real DB auto-increment id is a small positive integer (< 2 billion).
      // Date.now() fallbacks are ~1.7 trillion - reject those.
      const savedId = (result.id && Number(result.id) > 0 && Number(result.id) < 2000000000)
        ? result.id : null;

      if (!savedId) {
        // Show the actual server response so it is actionable
        const msg = result.error || result.raw || JSON.stringify(result);
        alert('Save failed.\n\nServer said: ' + msg + '\n\nCheck DevTools > Network > save_universal.php for the full request/response.');
        return;
      }

      // Keep full-res image in local state; compressed version was sent to API
      const newPart = { ...partData, image: imageData || compressedImage, id: savedId };

      if (editId) {
        setDb(prev => ({ ...prev, masterParts: (prev.masterParts || []).map(p => p.id === editId ? newPart : p) }));
      } else {
        setDb(prev => ({
          ...prev,
          masterParts: [
            ...(prev.masterParts || []).filter(p => String(p.id) !== String(savedId)),
            newPart
          ]
        }));
      }

      resetForm();
      setActiveView('gallery');
    } catch (e) {
      alert('Network error saving part: ' + (e.message || String(e)));
      console.error('PartDNA save error:', e);
    }
  };

  const resetForm = () => {
    setDna({ name: '', garmentType: '', fabricType: '', category: '', designAttr: '', image: '', dnaL: 20, dnaW: 15, processes: [], patterns: [], createdBy: '', createdOn: '', approvedBy: '', isApproved: false, specs: null });
    setProc({ name: '', attachment: '', video: '', purpose: '', ops: [], seamDiagramId: '', seamDiagramNote: '' });
    setEditId(null);
    setEditingProcessIdx(null);
    setImageSaved(false);
    setSketchMode('upload');
    setStudioStep(1);
  };

  const handleApprovePart = async () => {
    const approverName = window.prompt(
      `Approve "${dnaRef.current.name}"?

Enter your name as approver:`,
      dnaRef.current.approvedBy || ''
    );
    if (!approverName || !approverName.trim()) return;
    const approved = {
      ...dnaRef.current,
      approvedBy: approverName.trim(),
      approvedOn: new Date().toLocaleDateString(),
      isApproved: true
    };
    setDna(approved);
    // Save immediately so approval persists
    try {
      const res = await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'part', type: 'PART', data: editId ? { ...approved, id: editId } : approved })
      });
      const r = await res.json().catch(() => ({}));
      if (r.id) {
        setDb(prev => ({ ...prev, masterParts: (prev.masterParts || []).map(p =>
          (p.id === editId || p.id === r.id) ? { ...approved, id: r.id || editId } : p
        )}));
        alert(`Part approved by ${approverName.trim()}`);
      } else {
        alert('Approval recorded locally but server save failed: ' + (r.error || JSON.stringify(r)));
      }
    } catch (e) {
      alert('Network error during approval: ' + e.message);
    }
  };

  const handleEditPart   = (part) => { setDna(part); setEditId(part.id); setViewPart(null); setActiveView('studio'); };
  const handleDeletePart = async (id) => {
    // Remove from local state immediately so UI responds at once
    setDb(prev => ({ ...prev, masterParts: (prev.masterParts || []).filter(p => p.id !== id) }));
    // Only hit the API if this is a real DB id (not a Date.now() timestamp fallback)
    const isRealId = id && Number(id) > 0 && Number(id) < 2000000000;
    if (!isRealId) return;
    try {
      await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'part', type: 'PART', data: { id, _deleted: true } })
      });
    } catch (e) {
      console.error('Delete failed:', e);
    }
  };
  const handleCreateNew  = () => { resetForm(); setActiveView('studio'); };
  const handleClone      = (part) => {
    // Copy all data, clear id so it saves as new, append "(Copy)" to name
    setDna({
      ...part,
      name: `${part.name} (Copy)`,
      createdOn: new Date().toLocaleDateString(),
      approvedBy: '',
    });
    setEditId(null); // no editId = will create new record on save
    setViewPart(null);
    setActiveView('studio');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f5f6f8', overflow: 'hidden' }}>
      {/* -- Top Bar -- */}
      <div style={{ padding: '10px 20px', background: 'white', borderBottom: '1px solid #e6e9ef', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Scissors size={20} color="#0073ea" />
          <span style={{ fontWeight: 'bold', fontSize: '1.05rem', color: '#323338' }}>PART DNA</span>
          <div style={{ display: 'flex', gap: '4px', marginLeft: '10px' }}>
            {[{ id: 'gallery', label: 'Part Gallery' }, { id: 'studio', label: 'DNA Studio' }, { id: 'shapebuilder', label: '[tool] Shape Builder' }].map(v => (
              <button key={v.id} onClick={() => { if (v.id === 'studio' && !editId) resetForm(); setActiveView(v.id); }}
                style={{ padding: '6px 14px', border: 'none', borderRadius: '20px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: activeView === v.id ? 'bold' : 'normal', background: activeView === v.id ? '#0073ea' : '#f0f1f4', color: activeView === v.id ? 'white' : '#676879' }}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
        {activeView === 'studio' && (
          <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
            {dna.name
              ? <span style={{ fontSize:'0.82rem', fontWeight:'bold', color:'#323338' }}>{editId ? `\u270E ${dna.name}` : `\u271A New Part`}</span>
              : <span style={{ fontSize:'0.78rem', color:'#9699a6' }}>New Part</span>}
            {dna.isApproved && <span style={{ background:'#e3faf0', color:'#166534', fontSize:'0.68rem', padding:'2px 8px', borderRadius:10 }}>(/) APPROVED</span>}
            {dna.name && (
              <button onClick={() => setShareModal(dna)} style={{ padding:'5px 10px', background:'#8b5cf6', color:'white', border:'none', borderRadius:5, cursor:'pointer', display:'flex', gap:4, alignItems:'center', fontSize:'0.75rem' }}>
                <Send size={12} /> Share
              </button>
            )}
            {editId && (
              <button onClick={handleApprovePart}
                style={{ padding:'5px 10px', border:`1px solid ${dna.isApproved?'#86efac':'#b3f5d6'}`, borderRadius:5, cursor:'pointer', fontSize:'0.75rem', fontWeight:'bold',
                  background: dna.isApproved?'#e3faf0':'#f0fdf4', color: dna.isApproved?'#166534':'#15803d' }}>
                {dna.isApproved ? '(/) Approved' : 'Approve'}
              </button>
            )}
            <button onClick={resetForm} style={{ padding:'5px 10px', background:'#f0f1f4', color:'#676879', border:'none', borderRadius:5, cursor:'pointer', fontSize:'0.75rem' }}>Clear</button>
          </div>
        )}
      </div>

      {/* -- Views -- */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {activeView === 'gallery' && (
          <PartGallery db={db} onView={p => setViewPart(p)} onEdit={handleEditPart} onDelete={handleDeletePart} onCreateNew={handleCreateNew} onClone={handleClone} />
        )}

        {activeView === 'studio' && (
          <div style={{ height:'100%', display:'flex', flexDirection:'column', overflow:'hidden', background:'#f0f1f4' }}>

            {/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
                STEP WIZARD HEADER
                \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */}
            <div style={{ background:'white', borderBottom:'2px solid #e6e9ef', padding:'0 20px', flexShrink:0 }}>
              <div style={{ display:'flex', alignItems:'stretch', gap:0, height:44 }}>
                {[
                  { step:1, icon:'[list]', label:'IDENTITY'  },
                  { step:2, icon:'&#x270F;', label:'SKETCH'    },
                  { step:3, icon:'\u2699\uFE0F', label:'PROCESSES' },
                  { step:4, icon:'[patt]', label:'PATTERNS'  },
                ].map(({ step, icon, label }, idx) => {
                  const active  = studioStep === step;
                  const done    = studioStep > step;
                  return (
                    <React.Fragment key={step}>
                      {idx > 0 && <div style={{ width:1, background:'#e6e9ef', alignSelf:'stretch' }} />}
                      <button
                        onClick={() => setStudioStep(step)}
                        style={{
                          flex:1, border:'none', background:'transparent',
                          borderBottom: active ? '3px solid #0073ea' : '3px solid transparent',
                          cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center',
                          gap:6, fontSize:'0.72rem', fontWeight: active ? 'bold' : 'normal',
                          color: active ? '#0073ea' : done ? '#059669' : '#9699a6',
                          transition:'all 0.15s',
                        }}
                      >
                        <span style={{ fontSize:'0.9rem' }}>{done ? '(/)' : icon}</span>
                        <span style={{ letterSpacing:'0.05em' }}>{label}</span>
                        {active && <span style={{ width:6, height:6, borderRadius:'50%', background:'#0073ea', display:'inline-block' }} />}
                      </button>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
                STEP CONTENT
                \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */}
            <div style={{ flex:1, overflowY:'auto', minHeight:0 }}>

              {/* -- STEP 1: IDENTITY -- */}
              {studioStep === 1 && (
                <div style={{ padding:'20px', maxWidth:900, margin:'0 auto' }}>
                  <div style={{ background:'white', borderRadius:10, padding:20, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>

                    {/* -- Image First: upload zone visible while filling details -- */}
                    <div style={{ marginBottom:16, display:'flex', gap:16, alignItems:'flex-start' }}>
                      {/* Image upload / preview */}
                      <div style={{ flexShrink:0, width:160 }}>
                        <div style={{ fontSize:'0.7rem', fontWeight:'bold', color:'#9699a6', marginBottom:6, letterSpacing:'0.06em' }}>PART IMAGE</div>
                        <label style={{ cursor:'pointer', display:'block' }}>
                          <div style={{ width:160, height:160, border:`2px dashed ${dna.image ? '#0073ea' : '#c5c7d4'}`, borderRadius:8, overflow:'hidden', background:'#f5f6f8', display:'flex', alignItems:'center', justifyContent:'center', position:'relative' }}>
                            {dna.image ? (
                              <>
                                <img src={dna.image} alt="part" style={{ width:'100%', height:'100%', objectFit:'contain' }} />
                                <button onClick={e => { e.preventDefault(); setDna(p => ({ ...p, image:'' })); }}
                                  style={{ position:'absolute', top:4, right:4, background:'#e2445c', color:'white', border:'none', borderRadius:'50%', width:20, height:20, cursor:'pointer', fontSize:12, display:'flex', alignItems:'center', justifyContent:'center', padding:0 }}>x</button>
                              </>
                            ) : (
                              <div style={{ textAlign:'center', color:'#9699a6', padding:12 }}>
                                <div style={{ fontSize:28, marginBottom:4 }}>[cam]</div>
                                <div style={{ fontSize:'0.7rem' }}>Click to add<br/>part image</div>
                              </div>
                            )}
                          </div>
                          <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        </label>
                        {dna.image && (
                          <div style={{ marginTop:4, fontSize:'0.65rem', color:'#059669', textAlign:'center' }}>(/) Image ready</div>
                        )}
                        <div style={{ marginTop:4, fontSize:'0.65rem', color:'#9699a6', textAlign:'center' }}>
                          More options in Step 2 -&gt;
                        </div>
                      </div>

                      {/* Part Info Grid -- takes remaining width */}
                      <div style={{ flex:1 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#9699a6', marginBottom: '8px', letterSpacing: '0.06em' }}>PART DETAILS</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#9699a6', marginBottom: '8px', letterSpacing: '0.06em' }}>PART DETAILS</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
                  <div><label style={{ fontSize: '11px', color: '#676879' }}>Part Name *</label><input value={dna.name} onChange={e => setDna({ ...dna, name: e.target.value })} style={{ width: '100%', padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px', boxSizing: 'border-box' }} /></div>
                  <div><label style={{ fontSize: '11px', color: '#676879' }}>Garment Type</label>
                    <select value={dna.garmentType} onChange={e => setDna({ ...dna, garmentType: e.target.value })} style={{ width: '100%', padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px' }}>
                      <option value="">Select...</option>
                      {/* From Global Masters -> Products */}
                      {(db.products || []).map(p => p.name || p).filter(Boolean).map(g => <option key={g} value={g}>{g}</option>)}
                      {/* Fallback: unique values already used in parts library */}
                      {(db.products || []).length === 0 && [...new Set((db.parts || db.masterParts || []).map(p => p.garmentType).filter(Boolean))].map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                    {(db.products || []).length === 0 && <div style={{ fontSize:'0.65rem', color:'#9699a6', marginTop:2 }}>Add products in Admin -&gt; Global Masters</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#676879' }}>
                      Fabric Type {fabricsLoading && <span style={{ color: '#9699a6' }}>R loading...</span>}
                    </label>
                    <select value={dna.fabricType} onChange={e => {
                      const selected = fabricOptions.find(f => f.fabric === e.target.value);
                      setDna({ ...dna, fabricType: e.target.value, fabricDiffFactor: selected?.factor || 0 });
                    }} style={{ width: '100%', padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px' }}>
                      <option value="">Select...</option>
                      {fabricOptions.length === 0 && !fabricsLoading &&
                        <option disabled>- No fabrics in DB -</option>}
                      {fabricOptions.map(f => (
                        <option key={f.fabric} value={f.fabric}>
                          {f.fabric}{f.factor > 0 ? ` (${f.factor}% diff)` : ''}
                        </option>
                      ))}
                    </select>
                    {dna.fabricType && dna.fabricDiffFactor !== undefined && (
                      <div style={{ marginTop: '3px', fontSize: '0.68rem', background: '#fff5e6', color: '#92400e', padding: '2px 8px', borderRadius: '6px', display: 'inline-block' }}>
                        Difficulty Factor: {dna.fabricDiffFactor}%
                      </div>
                    )}
                  </div>
                  <div><label style={{ fontSize: '11px', color: '#676879' }}>Design / Attr</label>
                    <input value={dna.designAttr} onChange={e => setDna({ ...dna, designAttr: e.target.value })}
                      list="design-attr-list"
                      style={{ width: '100%', padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px', boxSizing: 'border-box' }} />
                    <datalist id="design-attr-list">
                      {[...new Set((db.parts || db.masterParts || []).map(p => p.designAttr).filter(Boolean))].map(d => <option key={d} value={d}/>)}
                    </datalist>
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#676879' }}>
                      Created By <span style={{ color: '#e2445c' }}>*</span>
                    </label>
                    <input value={dna.createdBy}
                      onChange={e => setDna({ ...dna, createdBy: e.target.value })}
                      placeholder="Your name (required)"
                      style={{ width: '100%', padding: '7px', border: `1px solid ${!dna.createdBy ? '#fca5a5' : '#e6e9ef'}`, borderRadius: '5px', boxSizing: 'border-box' }} />
                    {!dna.createdBy && <div style={{ fontSize: '0.65rem', color: '#e2445c', marginTop: '2px' }}>Required to save</div>}
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', color: '#676879', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      Approved By
                      {dna.isApproved && <span style={{ background: '#e3faf0', color: '#166534', fontSize: '0.6rem', padding: '1px 6px', borderRadius: '10px', fontWeight: 'bold' }}>APPROVED</span>}
                    </label>
                    <input value={dna.approvedBy}
                      onChange={e => setDna({ ...dna, approvedBy: e.target.value })}
                      placeholder={editId ? "Use Approve button above" : "Set after saving"}
                      readOnly={dna.isApproved}
                      style={{ width: '100%', padding: '7px', border: '1px solid #e6e9ef', borderRadius: '5px', boxSizing: 'border-box',
                        background: dna.isApproved ? '#f0fdf4' : 'white', color: dna.isApproved ? '#166534' : 'inherit' }} />
                  </div>
                </div>
              </div>
                      </div>{/* end Part Info Grid */}
                    </div>{/* end Image+Details flex row */}
                  </div>
                </div>
              )}

              {/* -- STEP 2: SKETCH & IMAGE -- */}
              {studioStep === 2 && (
                <div style={{ height:'100%', display:'flex', flexDirection:'column', padding:'16px', gap:'12px' }}>
                  <div style={{ background:'white', borderRadius:10, flex:1, minHeight:0, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', flexDirection:'column' }}>
                    {/* Sketch + Process side-by-side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>

                {/* -- LEFT: Sketch Panel -- */}
                <div style={sketchFullscreen
                  ? { position: 'fixed', inset: 0, zIndex: 1000, background: 'white', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', overflow: 'auto' }
                  : { display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#9699a6', letterSpacing: '0.06em' }}>
                      PART SKETCH {sketchFullscreen && <span style={{ color: '#0073ea', marginLeft: 8 }}>\u00B7 FULLSCREEN</span>}
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {[{ id: 'upload', label: '[file] Upload' }, { id: 'draw', label: '&#x270F; Smart Draw' }, { id: 'spec', label: '[spec] Spec Builder' }, { id: 'patterns', label: '[patt] Patterns & Specs' }].map(m => (
                        <button key={m.id} onClick={() => setSketchMode(m.id)}
                          style={{ padding: '4px 12px', fontSize: '11px', border: 'none', borderRadius: '20px', cursor: 'pointer', background: sketchMode === m.id ? '#0073ea' : '#f0f1f4', color: sketchMode === m.id ? 'white' : '#676879' }}>
                          {m.label}
                        </button>
                      ))}
                      <button
                        onClick={() => setSketchFullscreen(f => !f)}
                        title={sketchFullscreen ? 'Exit fullscreen' : 'Maximize canvas'}
                        style={{ padding: '4px 8px', marginLeft: 4, fontSize: '11px', border: 'none', borderRadius: '20px', cursor: 'pointer', background: sketchFullscreen ? '#0073ea' : '#f0f1f4', color: sketchFullscreen ? 'white' : '#676879', display: 'flex', alignItems: 'center', gap: 3 }}>
                        {sketchFullscreen ? <><Minimize2 size={11} /> Exit</> : <><Maximize2 size={11} /> Full</>}
                      </button>
                    </div>
                  </div>

                  {/* Upload mode */}
                  {sketchMode === 'upload' && (
                    <div style={{ border: '2px dashed #c5c7d4', borderRadius: '8px', minHeight: sketchFullscreen ? 'calc(100vh - 180px)' : '340px', flex: sketchFullscreen ? 1 : 'initial', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f6f8', position: 'relative', overflow: 'hidden' }}>
                      {dna.image ? (
                        <>
                          <img src={dna.image} style={{ maxWidth: '100%', maxHeight: sketchFullscreen ? 'calc(100vh - 200px)' : '340px', objectFit: 'contain' }} alt="part" />
                          <button onClick={() => setDna(prev => ({ ...prev, image: '' }))} style={{ position: 'absolute', top: 6, right: 6, background: '#e2445c', color: 'white', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={12} /></button>
                          <div style={{ position: 'absolute', bottom: 6, right: 6, background: '#e3faf0', color: '#166534', fontSize: '0.7rem', padding: '2px 8px', borderRadius: '6px' }}>(/) Saved to part</div>
                        </>
                      ) : (
                        <label style={{ cursor: 'pointer', textAlign: 'center', color: '#9699a6', padding: '30px' }}>
                          <ImageIcon size={40} style={{ margin: '0 auto 10px', display: 'block', opacity: 0.35 }} />
                          <div style={{ fontSize: '0.85rem', marginBottom: '6px' }}>Click to upload part image</div>
                          <div style={{ fontSize: '0.75rem', color: '#c5c7d4' }}>PNG, JPG, SVG accepted</div>
                          <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        </label>
                      )}
                    </div>
                  )}

                  {/* Remove White Background - shown whenever an image is loaded in upload mode */}
                  {sketchMode === 'upload' && dna.image && (
                    <div style={{ background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '7px', padding: '10px 12px', marginTop: '4px' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 'bold', color: '#92400e', marginBottom: '6px' }}>\u2702 Remove White Background</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.7rem', color: '#92400e', whiteSpace: 'nowrap' }}>Threshold:</span>
                        <input type="range" min={150} max={255} value={bgThreshold} onChange={e => setBgThreshold(+e.target.value)} style={{ flex: 1 }} />
                        <span style={{ fontSize: '0.72rem', color: '#78350f', minWidth: '28px', textAlign: 'right' }}>{bgThreshold}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button onClick={handleRemoveBackground} disabled={bgRemoving}
                          style={{ flex: 1, padding: '6px', background: bgRemoving ? '#fdab3d' : '#fdab3d', color: 'white', border: 'none', borderRadius: '5px', cursor: bgRemoving ? 'wait' : 'pointer', fontSize: '0.78rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                          <Scissors size={13} /> {bgRemoving ? 'Removing...' : 'Remove Background'}
                        </button>
                        <label style={{ padding: '6px 10px', background: '#323338', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <ImageIcon size={12} /> Replace
                          <input type="file" accept="image/*" hidden onChange={handleFileUpload} />
                        </label>
                      </div>
                      <div style={{ fontSize: '0.67rem', color: '#92400e', marginTop: '5px', opacity: 0.8 }}>
                        Higher threshold = more aggressive. Drag up if grey edges remain.
                      </div>
                    </div>
                  )}

                  {/* Draw mode - FIX #1: full-height canvas, FIX #2: real eraser, FIX #3: bezier/freeform, FIX #4: AI trace */}
                  {sketchMode === 'draw' && (
                    <div style={{ height: sketchFullscreen ? 'calc(100vh - 180px)' : '480px', border: '2px solid #c5c7d4', borderRadius: '8px', overflow: 'hidden' }}>
                      <SketchCanvas onSave={handleSketchSave} initialImage={dna.image || null} />
                    </div>
                  )}

                  {/* Spec Builder mode -- parametric garment-panel generator */}
                  {sketchMode === 'spec' && (
                    <div style={{ height: sketchFullscreen ? 'calc(100vh - 180px)' : '560px', border: '2px solid #c5c7d4', borderRadius: '8px', overflow: 'hidden' }}>
                      <SpecDrivenShape
                        onSave={handleSketchSave}
                        initialSpecs={dna.specs ? { ...dna.specs, referenceImage: dna.image } : { referenceImage: dna.image }}
                        initialTemplate={dna.specs?.template}
                        onSpecsChange={(specs) => setDna(prev => ({ ...prev, specs }))}
                      />
                    </div>
                  )}

                  {/* Status badge for Spec mode */}
                  {dna.image && sketchMode === 'spec' && (
                    <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '6px', padding: '6px 12px', fontSize: '0.78rem', color: '#4338ca', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      [spec] Shape + {dna.specs?.measurements ? Object.keys(dna.specs.measurements).length : 0} measurements saved to part -- click "Save Shape to Part" in the panel whenever you change specs
                    </div>
                  )}

                  {/* FIX #5: Clear status badge showing image is in the part */}
                  {dna.image && sketchMode === 'draw' && (
                    <div style={{ background: '#e3faf0', border: '1px solid #86efac', borderRadius: '6px', padding: '6px 12px', fontSize: '0.78rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      (/) Sketch saved to part - will be stored in Part Bank when you click "Save to Library"
                    </div>
                  )}

                  {/* -- Patterns & Specs mode -- */}
                  {sketchMode === 'patterns' && (
                    <div style={{ border: '2px solid #ddd6fe', borderRadius: '8px', overflow: 'hidden', minHeight: '340px' }}>
                      <PatternSection dna={dna} />
                    </div>
                  )}
                </div>

                {/* -- RIGHT: Process Builder -- */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#9699a6', letterSpacing: '0.06em' }}>
                      {editingProcessIdx !== null ? `&#x270F; EDITING: ${proc.name || '...'}` : '+ BUILD PROCESS'}
                    </div>
                    {editingProcessIdx !== null && (
                      <button onClick={() => { setEditingProcessIdx(null); setProc({ name: '', attachment: '', video: '', purpose: '', ops: [], seamDiagramId: '', seamDiagramNote: '' }); }}
                        style={{ fontSize: '0.72rem', color: '#e2445c', background: 'none', border: 'none', cursor: 'pointer' }}>(x) Cancel Edit</button>
                    )}
                  </div>
                  <OpBuilderPanel proc={proc} setProc={setProc} db={db} />
                  {proc.video === '' && (
                    <label style={{ fontSize: '0.72rem', color: '#676879', cursor: 'pointer', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <PlayCircle size={13} color="#0073ea" /> Upload video file:
                      <input type="file" accept="video/*" hidden onChange={handleVideoUpload} />
                      <span style={{ color: '#0073ea', textDecoration: 'underline' }}>Browse</span>
                    </label>
                  )}
                  <button onClick={addOrUpdateProcess}
                    style={{ padding: '10px', background: editingProcessIdx !== null ? '#fdab3d' : '#323338', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>
                    {editingProcessIdx !== null ? '(/) UPDATE PROCESS' : '+ ADD PROCESS TO PART'}
                  </button>

                  {/* -- Live process preview -- edit without leaving Step 2 -- */}
                  {dna.processes.length > 0 && (
                    <div style={{ marginTop:8, border:'1px solid #e6e9ef', borderRadius:7, overflow:'hidden' }}>
                      <div style={{ background:'#323338', color:'white', padding:'6px 10px', fontSize:'0.7rem', fontWeight:'bold', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <span>(/) {dna.processes.length} PROCESS(ES) ADDED \u00B7 Total SMV: {totalSMV.toFixed(3)} min</span>
                        <span style={{ color:'#9ece6a', fontSize:'0.65rem' }}>Click \u270E to edit inline</span>
                      </div>
                      {dna.processes.map((pr, pi) => {
                        const smv     = calcProcessSMV(pr, db);
                        const mcOp    = pr.ops?.find(o => o.type === 'machine');
                        const mcCode  = mcOp ? (db.machines?.find(m => m.id == mcOp.mcId)?.code || 'SEW') : 'MAN';
                        const isEditing = editingProcessIdx === pi;
                        const procCode  = pr.procCode || `P${String(pi + 1).padStart(3, '0')}`;
                        return (
                          <div key={pi} style={{ padding:'7px 10px', borderBottom:'1px solid #f0f1f4', background: isEditing ? '#fffbeb' : (pi%2===0?'white':'#f9f9f9'), display:'flex', alignItems:'center', gap:8, fontSize:'0.78rem' }}>
                            <span style={{ color:'#9699a6', fontFamily:'monospace', fontSize:'0.7rem', flexShrink:0, minWidth:38 }}>{procCode}</span>
                            <span style={{ fontWeight:600, flex:1 }}>{pr.name}</span>
                            {pr.attachment && <span style={{ color:'#676879', fontSize:'0.7rem' }}>[clip] {pr.attachment}</span>}
                            <span style={{ background:'#e8f0ff', color:'#0073ea', padding:'1px 6px', borderRadius:3, fontWeight:'bold', fontSize:'0.68rem', flexShrink:0 }}>{mcCode}</span>
                            {pr.seamDiagramId && (() => {
                              const sd = (db.seamDiagrams||[]).find(s=>String(s.id)===String(pr.seamDiagramId));
                              return sd ? <span title={sd.name} style={{ background:'#e0e7ff', color:'#4338ca', padding:'1px 6px', borderRadius:3, fontSize:'0.65rem', flexShrink:0 }}>~{sd.code||sd.name.slice(0,4)}</span> : null;
                            })()}
                            <span style={{ color:'#059669', fontWeight:'bold', fontFamily:'monospace', flexShrink:0 }}>{smv.toFixed(3)}</span>
                            <button onClick={() => editProcess(pi)} title="Edit this process"
                              style={{ padding:'2px 7px', background: isEditing?'#fdab3d':'#fff5e6', border:'none', borderRadius:4, cursor:'pointer', color: isEditing?'white':'#fdab3d', fontSize:'0.72rem', flexShrink:0 }}>
                              {isEditing ? '\u270E editing' : '\u270E'}
                            </button>
                            <button onClick={() => deleteProcess(pi)} title="Remove"
                              style={{ padding:'2px 6px', background:'#fef2f2', border:'none', borderRadius:4, cursor:'pointer', color:'#e2445c', fontSize:'0.72rem', flexShrink:0 }}>(x)</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
                  </div>
                </div>
              )}

              {/* -- STEP 3: PROCESSES & SMV -- */}
              {studioStep === 3 && (
                <div style={{ padding:'16px', maxWidth:1200, margin:'0 auto' }}>
                  <div style={{ background:'white', borderRadius:10, padding:16, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
                    {/* -- SMV / OB Breakdown with live efficiency targets -- */}
              {(() => {
                const pitch     = dna.processes.length > 0 ? totalSMV / dna.processes.length : 0;
                const dailyTgt  = totalSMV > 0 ? Math.floor((480 / totalSMV) * (targetEff / 100)) : 0;
                const hourlyTgt = totalSMV > 0 ? parseFloat(((60 / totalSMV) * (targetEff / 100)).toFixed(1)) : 0;
                return (
                  <div style={{ border: '1px solid #e6e9ef', borderRadius: '8px', overflow: 'hidden' }}>

                    {/* -- Header + KPI bar -- */}
                    <div style={{ background: '#323338', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '0.85rem', color: 'white' }}>
                        SMV / OPERATION BULLETIN - {dna.processes.length} process(es)
                      </span>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.78rem', color: '#9699a6' }}>
                          Planned Eff %:
                          <input
                            type="number" min={1} max={150} value={targetEff}
                            onChange={e => setTargetEff(Math.max(1, Math.min(150, +e.target.value)))}
                            style={{ width: '52px', padding: '3px 6px', border: 'none', borderRadius: '6px', fontSize: '0.85rem', textAlign: 'center', fontWeight: 'bold', color: '#323338' }}
                          />
                        </label>
                        {[
                          ['Total SMV',   totalSMV.toFixed(4) + ' min', '#4ade80'],
                          ['Pitch Time',  pitch.toFixed(3)    + ' min', '#5ba4f5'],
                          ['Hourly',      hourlyTgt + ' pcs',            '#fdab3d'],
                          ['Daily (8 h)', dailyTgt  + ' pcs',            '#fb923c'],
                        ].map(([k, v, col]) => (
                          <div key={k} style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '5px', padding: '4px 10px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.6rem', color: '#9699a6', textTransform: 'uppercase' }}>{k}</div>
                            <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: col }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* -- Empty state -- */}
                    {dna.processes.length === 0 && (
                      <div style={{ padding: '24px', textAlign: 'center', color: '#9699a6', fontSize: '0.85rem', background: '#f5f6f8' }}>
                        No processes yet. Build a process above and click "+ Add Process to Part".
                      </div>
                    )}

                    {/* -- OB table -- */}
                    {dna.processes.length > 0 && (
                      <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem', minWidth: '820px' }}>
                          <thead>
                            <tr style={{ background: '#0073ea', color: 'white', position: 'sticky', top: 0, zIndex: 5 }}>
                              {['#', 'Process Name', 'Machine', 'Ops', 'SMV (min)', 'Workers', 'Hrly Target', 'Actions'].map(h => (
                                <th key={h} style={{ padding: '8px 10px', textAlign: ['#','Ops','SMV (min)','Workers','Hrly Target'].includes(h) ? 'right' : 'left', whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {dna.processes.map((pr, pi) => {
                              const smv       = calcProcessSMV(pr, db);
                              const workers   = pitch > 0 ? parseFloat((smv / pitch).toFixed(2)) : 0;
                              const procHrly  = smv > 0 ? parseFloat(((60 / smv) * (targetEff / 100)).toFixed(1)) : 0;
                              const mcOp      = pr.ops?.find(o => o.type === 'machine');
                              const mcCode    = mcOp ? (db.machines?.find(m => m.id == mcOp.mcId)?.code || 'SEW') : 'MANUAL';
                              return (
                                <React.Fragment key={pi}>
                                  {/* -- Row -- */}
                                  <tr style={{ borderBottom: '1px solid #e6e9ef', background: pi % 2 === 0 ? 'white' : '#f5f6f8' }}>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', color: '#9699a6', width: '32px' }}>
                                      <div>{pi + 1}</div>
                                      <div style={{ fontSize:'0.6rem', fontFamily:'monospace', color:'#0073ea', marginTop:1 }}>{pr.procCode || `P${String(pi+1).padStart(3,'0')}`}</div>
                                    </td>
                                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {pr.name}
                                        {pr.purpose && <span title={pr.purpose} style={{ fontSize:'0.65rem', color:'#9699a6', fontStyle:'italic', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>-- {pr.purpose}</span>}
                                        {pr.video && (
                                          <button onClick={() => setVideoModal(pr.video)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#0073ea', display: 'flex', alignItems: 'center', gap: '2px', fontSize: '0.7rem', padding: 0 }}>
                                            <PlayCircle size={12} /> Video
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                    <td style={{ padding: '8px 10px' }}>
                                      <span style={{ background: '#e8f0ff', color: '#0073ea', padding: '2px 6px', borderRadius: '3px', fontSize: '0.72rem', fontWeight: 'bold' }}>{mcCode}</span>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', color: '#676879' }}>{pr.ops?.length || 0}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', color: '#059669' }}>{smv.toFixed(4)}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                                      <span style={{ background: workers > 1.2 ? '#fff5e6' : '#e3faf0', color: workers > 1.2 ? '#92400e' : '#166534', padding: '2px 7px', borderRadius: '3px', fontWeight: 'bold', fontSize: '0.78rem' }}>
                                        {workers}
                                      </span>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', color: '#fdab3d' }}>{procHrly}</td>
                                    <td style={{ padding: '6px 10px' }}>
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button onClick={() => editProcess(pi)} title="Edit" style={{ padding: '3px 6px', background: '#fff5e6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#fdab3d' }}><Edit2 size={11} /></button>
                                        <button onClick={() => setPrintProcess({ process: pr, part: dna })} title="Print Card" style={{ padding: '3px 6px', background: '#e8f0ff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#0073ea' }}><Printer size={11} /></button>
                                        <button onClick={() => setShareModal({ ...dna, shareProcess: pr })} title="Share" style={{ padding: '3px 6px', background: '#f3e8ff', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#a25ddc' }}><Send size={11} /></button>
                                        <button onClick={() => deleteProcess(pi)} title="Delete" style={{ padding: '3px 6px', background: '#fef2f2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#e2445c' }}><Trash2 size={11} /></button>
                                      </div>
                                    </td>
                                  </tr>
                                  {/* -- Expandable ops detail -- */}
                                  <tr style={{ background: '#fafafa' }}>
                                    <td colSpan={8} style={{ padding: 0 }}>
                                      <details>
                                        <summary style={{ padding: '3px 10px 3px 36px', fontSize: '0.68rem', color: '#9699a6', cursor: 'pointer', listStyle: 'none' }}>
                                          \u25B6 {pr.ops?.length || 0} op(s) \u00B7 Cont 3% \u00B7 P&F 11% \u00B7 Bundle 0.09 min
                                        </summary>
                                        <div style={{ padding: '8px 16px 12px' }}>
                                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                            <thead>
                                              <tr style={{ background: '#f0f1f4' }}>
                                                {['Type', 'Code / Machine', 'Length', 'Freq', 'Stop%', 'Fab%', 'Direct(s)', 'Basic(s)'].map(h => (
                                                  <th key={h} style={{ padding: '4px 8px', textAlign: ['Length','Freq','Stop%','Fab%','Direct(s)','Basic(s)'].includes(h) ? 'right' : 'left' }}>{h}</th>
                                                ))}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(pr.ops || []).map((o, oi) => {
                                                const mc = db.machines?.find(m => m.id == o.mcId);
                                                const mn = db.manualCodes?.find(m => m.id == o.refId);
                                                return (
                                                  <tr key={oi} style={{ borderBottom: '1px solid #f0f1f4' }}>
                                                    <td style={{ padding: '4px 8px' }}>{o.type}</td>
                                                    <td style={{ padding: '4px 8px' }}>{o.type === 'machine' ? mc?.code : `${mn?.code} - ${mn?.desc || ''}`}</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{o.length || '-'}</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{o.frequency || 1}</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{o.stoppage || 0}%</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{o.fabric || 0}%</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{o.directTime || '-'}</td>
                                                    <td style={{ padding: '4px 8px', textAlign: 'right', fontWeight: 'bold' }}>{calcBasicSecs(o, db.machines, db.manualCodes).toFixed(3)}</td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </details>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                          {/* -- Totals footer -- */}
                          <tfoot>
                            <tr style={{ background: '#323338', color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>
                              <td colSpan={4} style={{ padding: '8px 10px' }}>TOTALS @ {targetEff}% efficiency</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: '#4ade80' }}>{totalSMV.toFixed(4)}</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: '#5ba4f5' }}>
                                {dna.processes.reduce((s, pr) => {
                                  const smv = calcProcessSMV(pr, db);
                                  return s + (pitch > 0 ? parseFloat((smv / pitch).toFixed(2)) : 0);
                                }, 0).toFixed(2)}
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fdab3d' }}>{hourlyTgt} pcs/hr</td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', color: '#fb923c' }}>Daily: {dailyTgt} pcs</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
                  </div>
                </div>
              )}

              {/* -- STEP 4: PATTERNS & SPECS -- */}
              {studioStep === 4 && (
                <div style={{ height:'100%', display:'flex', flexDirection:'column', padding:'16px', gap:0, minHeight:0 }}>
                  <div style={{ background:'white', borderRadius:10, flex:1, minHeight:0, overflow:'hidden', boxShadow:'0 1px 4px rgba(0,0,0,0.06)', display:'flex', flexDirection:'column' }}>
                    <PatternSection dna={dna} />
                  </div>
                </div>
              )}

            </div>

            {/* \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550
                BOTTOM NAV BAR -- step navigation + save
                \u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550\u2550 */}
            <div style={{ background:'white', borderTop:'1px solid #e6e9ef', padding:'10px 20px', display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
              <button
                onClick={() => studioStep > 1 ? setStudioStep(s => s - 1) : setActiveView('gallery')}
                style={{ padding:'8px 18px', background:'#f0f1f4', color:'#676879', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.82rem', display:'flex', alignItems:'center', gap:6 }}
              >
                &larr; {studioStep > 1 ? 'Previous' : 'Back to Gallery'}
              </button>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <span style={{ fontSize:'0.75rem', color:'#9699a6' }}>Step {studioStep} of 4</span>
                {dna.processes.length > 0 && (
                  <span style={{ fontSize:'0.75rem', background:'#e3faf0', color:'#166534', padding:'3px 10px', borderRadius:4 }}>
                    SMV: {totalSMV.toFixed(3)} min
                  </span>
                )}
              </div>
              {studioStep < 4 ? (
                <button
                  onClick={() => setStudioStep(s => s + 1)}
                  disabled={studioStep === 1 && !dna.name.trim()}
                  style={{ padding:'8px 20px', background: (studioStep===1 && !dna.name.trim()) ? '#e6e9ef' : '#0073ea', color: (studioStep===1 && !dna.name.trim()) ? '#9699a6' : 'white', border:'none', borderRadius:6, cursor:(studioStep===1&&!dna.name.trim())?'not-allowed':'pointer', fontSize:'0.82rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:6 }}
                >
                  Next -&gt; {['','SKETCH','PROCESSES','PATTERNS'][studioStep]}
                </button>
              ) : (
                <div style={{ display:'flex', gap:8 }}>
                  <button
                    onClick={async () => { await finalize(); }}
                    style={{ padding:'8px 20px', background:'#059669', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.82rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:6 }}
                  >
                    [save] Save to Library
                  </button>
                  <button
                    onClick={async () => {
                      await finalize();
                      // finalize resets form and goes to gallery; override to stay in studio with fresh form
                      setTimeout(() => { resetForm(); setActiveView('studio'); setStudioStep(1); }, 200);
                    }}
                    title="Save this part then immediately start a new one"
                    style={{ padding:'8px 16px', background:'#0073ea', color:'white', border:'none', borderRadius:6, cursor:'pointer', fontSize:'0.82rem', fontWeight:'bold', display:'flex', alignItems:'center', gap:5 }}
                  >
                    [save]+ Save & New
                  </button>
                </div>
              )}
            </div>

          </div>
        )}

        {activeView === 'shapebuilder' && (
          <ShapeBuilder
            db={db}
            setDb={setDb}
            onBack={() => setActiveView('gallery')}
            onSavePartToDNA={async (partRecord) => {
              // Reuse existing save path: save_universal.php treats parts as category='part'
              const id = `part-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
              const newPart = {
                id,
                name: partRecord.name,
                garmentType: partRecord.garmentType || 'Component',
                fabricType: '',
                category: '',
                designAttr: partRecord.designAttr || '',
                image: partRecord.image || '',
                dnaL: 20, dnaW: 15,
                processes: partRecord.processes || [],
                patterns: partRecord.patterns || [],
                specs: partRecord.specs || null,
                createdBy: partRecord.createdBy || 'ShapeBuilder',
                createdOn: new Date().toLocaleDateString(),
                approvedBy: '', isApproved: false,
              };
              // 1) Update React state so it's immediately visible in Gallery
              setDb(prev => ({ ...prev, masterParts: [...(prev.masterParts || []), newPart] }));
              // 2) Persist to backend
              try {
                if (typeof saveToDB === 'function') {
                  await saveToDB('part', newPart);
                } else {
                  await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ category: 'part', data: newPart })
                  });
                }
              } catch (e) {
                console.warn('Backend save failed (kept in session):', e);
              }
              return id;
            }}
            onSaveGarmentAsStyle={async (garment) => {
              const id = `style-${Date.now()}-${Math.random().toString(36).slice(2,7)}`;
              const newStyle = {
                id,
                name: garment.name,
                parts: garment.parts || [],
                createdBy: garment.createdBy || 'ShapeBuilder',
                createdOn: new Date().toLocaleDateString(),
                source: 'shapebuilder',
              };
              setDb(prev => ({ ...prev, masterStyles: [...(prev.masterStyles || []), newStyle] }));
              try {
                if (typeof saveToDB === 'function') {
                  await saveToDB('style', newStyle);
                } else {
                  await fetch('https://nirvikalp.integrashion.com/api/save_universal.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ category: 'style', data: newStyle })
                  });
                }
              } catch (e) {
                console.warn('Backend save failed (kept in session):', e);
              }
              return id;
            }}
          />
        )}
      </div>

      {/* Modals */}
      {smartClone && (
        <SmartCloneModal
          newImage={smartClone.newImage}
          newPartName={dna.name || smartClone.newPartName}
          allParts={db.masterParts || db.parts || []}
          db={db}
          onClone={(cloned) => {
            setDna(prev => ({
              ...prev,
              garmentType:    cloned.garmentType    || prev.garmentType,
              fabricType:     cloned.fabricType     || prev.fabricType,
              designAttr:     cloned.designAttr     || prev.designAttr,
              processes:      cloned.processes      || [],
              clonedFrom:     cloned.clonedFrom,
              clonedFromName: cloned.clonedFromName,
            }));
            setSmartClone(null);
          }}
          onSkip={() => setSmartClone(null)}
        />
      )}
      {videoModal    && <VideoModal url={videoModal} onClose={() => setVideoModal(null)} />}
      {printProcess  && <PrintProcessCard process={printProcess.process} part={printProcess.part} db={db} onClose={() => setPrintProcess(null)} />}
      {viewPart      && <PartDetailView part={viewPart} db={db} onClose={() => setViewPart(null)} onEdit={handleEditPart} />}
      {shareModal    && <ShareModal part={shareModal} db={db} onClose={() => setShareModal(null)} />}
    </div>
  );
}
